import {Component, OnInit, ViewChild, OnDestroy, ElementRef} from '@angular/core';
import BusinessEntity from '../../../../services/businessEntity.model';
import FarmSiteModel from '../../../../services/farmSite.model';
import CustomerPondModel from '../../../../services/customerPond.model';
import PondSectionModel from '../../../../services/pondSection.model';
import MetricsModel from '../../../../services/metrics.model';
import {MetricsService} from '../../../../services/metrics.service';
import {BusinessEntityService} from '../../../../services/businessEntity.service';
import {FarmSiteService} from '../../../../services/farmSite.service';
import {CustomerPondService} from '../../../../services/customerPond.service';
import {PondSectionService} from '../../../../services/pondSection.service';
import * as _ from 'lodash';
import moment from 'moment';;
import Chart from 'chart.js';
import {ActivatedRoute} from '@angular/router';
import {PondCultureService} from '../../../../services/pondCulture.service';
import PondCultureModel from '../../../../services/pondCulture.model';
import {ToastrService} from 'ngx-toastr';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';

declare var $: any;

/**
 * @component AnalyticsComponent
 * @selector app-analytics
 * @implements {OnInit}
 * @implements {OnDestroy}
 *
 * @description
 * The `AnalyticsComponent` serves as a comprehensive dashboard for visualizing farm analytics.
 * It provides graphical representations of various metrics related to water quality and aquaculture growth.
 * Users can view data across different time ranges, compare parameters, and analyze trends for specific ponds.
 *
 * ## Key Responsibilities:
 * 1. **Data Visualization**: Renders interactive line charts for Water Quality parameters (pH, DO, Temperature, etc.) and Growth parameters (ABW, ADG, FCR, etc.).
 * 2. **Hierarchical Selection**: Allows users to drill down from Business -> Farm Site -> Section -> Pond.
 * 3. **Tabular Views**:
 *    - **Readings**: Focuses on water quality metrics.
 *    - **Growth**: Focuses on biological growth metrics.
 *    - **Compare**: Allows comparison of two different parameters (e.g., pH vs Growth) for a specific pond.
 * 4. **Filtering**: Supports filtering by date ranges (Last 7 days, 30 days, 100 days, Today) and by specific culture cycles.
 * 5. **Comparison**: Enables side-by-side comparison of different metrics to identify correlations.
 * 6. **Role-Based Access**: Adapts data loading and visibility based on user roles (Farmer, Farm Manager, Business Admin, Employee).
 *
 * ## User Roles & Permissions:
 * - **Farm Category Users**:
 *   - **Business Admin / Farmer**: Access to all farm sites and analytics for their business.
 *   - **Farm Manager**: Restricted access to farm sites assigned to them.
 * - **Other Categories**:
 *   - **Employee**: Can search for and select businesses to view analytics.
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessCategory`: Business context.
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `loginBusinessName`: Used for display.
 * - `LoginUserName`: Used for display.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` loads user context and initial farm sites/businesses.
 * 2. **Selection**: User selects Farm Site -> Section -> Pond(s).
 * 3. **Data Fetching**: `getDbData` calls `MetricsService` to fetch all metrics for the selected farm site.
 * 4. **Filtering**: `prepareGlobalData` filters the fetched metrics based on the selected date range (`noOfDays`) or culture cycle.
 * 5. **Chart Preparation**: `newMethod` (and `popupNewMethod`) processes the filtered data into Chart.js datasets based on selected parameters (e.g., `ph`, `temp`).
 * 6. **Rendering**: Specific chart methods (e.g., `pHChart`, `tempChart`) render the canvases.
 *
 * ## UI State Management:
 * - **Tabs**: `tabIndex` controls the active view (Readings/Growth/Compare).
 * - **Loading**: `pgLoad` manages the loading spinner.
 * - **Chart Visibility**: Numerous boolean flags (e.g., `phChartDisp`, `abwChartDisp`) control which charts are rendered.
 * - **Modals**: Manages popup modals for detailed chart views and comparisons.
 */
@Component({
  selector: 'app-analytics',
  templateUrl: './analytics.component.html',
  styleUrls: ['./analytics.component.scss']
})
export class AnalyticsComponent implements OnInit, OnDestroy{

  /** @type {Array<BusinessEntity>} List of available business entities for selection. */
  public businessEntites: Array<BusinessEntity>;

  /** @type {Array<FarmSiteModel>} List of farm sites associated with the selected business. */
  public farmsites: Array<FarmSiteModel>;

  /** @type {Array<CustomerPondModel>} List of customer ponds for the selected section. */
  public customerPonds: Array<CustomerPondModel>;

  /** @type {Array<PondSectionModel>} List of sections within the selected farm site. */
  public pondSections: Array<PondSectionModel>;

  /** @type {Array<PondCultureModel>} List of cultures associated with the selected pond. */
  public pondCUltures: Array<PondCultureModel>;

  /** @type {PondCultureModel} Selected pond culture model (unused in current logic). */
  public pondCUlture: PondCultureModel;

  /** @type {Array<MetricsModel>} List of metrics data fetched from the service. */
  public metrics: Array<MetricsModel>;

  /** @type {MetricsModel} Model used for binding filter criteria. */
  public metric: MetricsModel;

  // ViewChild references for Chart Canvases
  @ViewChild('lineCanvas') lineCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('barCanvas') barCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('pHCanvas') pHCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('tempCanvas') tempCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('salinityCanvas') salinityCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('uammoniaCanvas') uammoniaCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('alkalinityCanvas') alkalinityCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('bioflocCanvas') bioflocCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('bicorbonateCanvas') bicorbonateCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('calciumCanvas') calciumCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('carbonateCanvas') carbonateCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('doCanvas') doCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('hardnessCanvas') hardnessCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('magnesiumCanvas') magnesiumCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('nitriteCanvas') nitriteCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('orpCanvas') orpCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('turbidityCanvas') turbidityCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('tanCanvas') tanCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('tdsCanvas') tdsCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('abwCanvas') abwCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('adgCanvas') adgCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('srCanvas') srCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('tbmCanvas') tbmCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('fcrCanvas') fcrCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('dcfCanvas') dcfCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('compareCanvas') compareCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('comparepopUpCanvas') comparepopUpCanvas: ElementRef<HTMLCanvasElement>;

  /** @type {number} Current tab index (0: Readings, 1: Growth, 2: Compare). */
  public tabIndex: number = 0;

  /** @type {string} Role of the logged-in user. */
  public loginPersonType: string;

  /** @type {string} Name of the logged-in user. */
  public LoginUserName: string;

  /** @type {string} Name of the logged-in user's business. */
  public loginUserBusinessName: string;

  /** @type {string} Category of the logged-in user's business. */
  public loginUserBusinessCategory: string;

  /** @type {Array<any>} Dropdown list for active pond details. */
  public activepondsDetails_dropdown = new Array();

  /** @type {string} Current date string. */
  public currentDay: string;

  /** @type {boolean} Flag for page loading state. */
  public pgLoad: boolean = false;

  /** @type {string} Selected date range filter ('seven', 'thirty', 'hundred', 'today'). */
  public noOfDays: string;

  /** @type {boolean} Flag indicating if filtering by culture cycle (true) or date range (false). */
  public dayOrCulture: boolean;

  /** @type {Array<any>} Array of checked pond IDs for multi-selection. */
  public checkedIDs = new Array();

  // Water readings chart display flags
  public phChartDisp = false; //water readings variables declaration
  public uammoniaChartDisp = false;
  public alkalinityChartDisp = false;
  public bioflocChartDisp = false;
  public calciumChartDisp = false;
  public carbonateChartDisp = false;
  public doChartDisp = false;
  public hardnessChartDisp = false;
  public magnesimChartDisp = false;
  public nitriteChartDisp = false;
  public orpChartDisp = false;
  public salinityChartDisp = false;
  public turbidityChartDisp = false;
  public tanChartDisp = false;
  public tdsChartDisp = false;
  public tempChartDisp = false;
  public bicorbonateDisp = false;

  // Growth chart display flags
  public abwChartDisp = false; // growth variables declaration
  public adgChartDisp = false;
  public srChartDisp = false;
  public tbmChartDisp = false;
  public fcrChartDisp = false;
  public dcfChartDisp = false;

  /** @type {boolean} Flag to display the comparison chart. */
  public compareChartDisplay = false; // comparegraphdisplay

  /** @type {Array<any>} Global data array for charts (filtered). */
  public chartGlobalData = new Array();

  /** @type {Array<any>} Global data array for popup charts. */
  public popupchartGlobalData = new Array();

  /** @type {string} Date string for 7 days ago. */
  public sevenfromDate: string;

  /** @type {string} Date string for 30 days ago. */
  public thirtyfromDate: string;

  /** @type {string} Date string for 100 days ago. */
  public hundreadfromDate: string;

  /** @type {string} Date string for today. */
  public fromDate: string;

  /** @type {boolean} Flag indicating if search/data fetch has occurred. */
  public isSearchClicked: boolean;

  /** @type {Array<any>} Array of resultant readings (unused in current logic). */
  public resultantReadings = new Array();

  /** @type {Array<any>} Filtered data based on date range. */
  public filteredData = new Array();

  /** @type {any} Chart.js instance for line chart. */
  public lineChart: any;

  /** @type {any} Chart.js instance for bar chart. */
  public barChart: any;

  /** @type {any} Selected growth parameter for comparison. */
  public growthParam: any;

  /** @type {any} Selected reading parameter for comparison. */
  public readingParam: any;

  /** @type {string} Generic parameter holder. */
  public param: string;

  /** @type {string} Generic parameter holder. */
  public param1: string;

  /** @type {any[]} Temporary array for comparison parameters. */
  public temp: any[];

  /** @type {boolean} Flag for adding comparison (unused). */
  public addCompare: boolean;

  /** @type {boolean} Flag for removing comparison (unused). */
  public removeCompare: boolean;

  // Flags for selecting parameters in Readings tab
  public phreading: boolean; // tabindex == 2 declarations
  public doreading: boolean;
  public salinityreading: boolean;
  public tempreading: boolean;
  public tanreading: boolean;
  public uammoniareading: boolean;
  public nitritereading: boolean;
  public alkalinityreading: boolean;
  public bioflocreading: boolean;

  // Flags for selecting parameters in Growth tab
  public abwGrowth: boolean;
  public adgGrowth: boolean;
  public srGrowth: boolean;
  public tbmGrowth: boolean;
  public fcrGrowth: boolean;
  public dcfGrowth: any;

  /** @type {any[]} Temporary array for popup comparison parameters. */
  public compareTemp: any [];

  /** @type {any[]} List of selected items from dropdown. */
  public selectedItemsList: any[];

  /** @type {boolean} Flag to display comparison popup chart. */
  public comparePopUpChartDisplay: boolean;

  /** @type {boolean} Flag indicating if view button was clicked. */
  public viewclicked: boolean;

  // Flags for selecting parameters in Popup
  public pHPopUpreading: boolean; // popup param ngmodal declarations
  public doPopUpreading: boolean;
  public salinityPopUpreading: boolean;
  public tempPopUpreading: boolean;
  public tanPopUpreading: boolean;
  public uammoniaPopUpreading: boolean;
  public nitritePopUpreading: boolean;
  public alkalinityPopUpreading: boolean;
  public bioflocPopUpreading: boolean;
  public abwPopUpGrowth: boolean;
  public adgPopUpGrowth: boolean;
  public srPopUpGrowth: boolean;
  public tbmPopUpGrowth: boolean;
  public fcrPopUpGrowth: boolean;
  public dcfPopUpGrowth: boolean;

  // popup variables declaration
  /** @type {string} Keyword for search. */
  public keyword: string;

  /** @type {any} Generic item holder. */
  public item: any;

  // Chart data objects for modals
  public phChartModalData: any;
  public tempChartModalData: any;
  public salinityChartModalData: any;
  public ammoniaChartModalData: any;
  public doChartModalData: any;
  public alkalinityChartModalData: any;
  public bioflocChartModalData: any;
  public calciumChartModalData: any;
  public hardnessChartModalData: any;
  public magnesiumChartModalData: any;
  public nitriteChartModalData: any;
  public carbonateChartModalData: any;
  public tanChartModalData: any;
  public tdsChartModalData: any;
  public turbidityChartModalData: any;
  public orpChartModalData: any;
  public abwChartModalData: any;
  public adgChartModalData: any;
  public srChartModalData: any;
  public tbmChartModalData: any;
  public fcrChartModalData: any;
  public dcfChartModalData: any;
  public biCarbonateChartModalData: any;

  /** @type {any} Application permissions object. */
  public appPermission: any;

  /** @type {any[]} Array of pond names (unused). */
  public pondName: any[];

  /**
   * Constructor for AnalyticsComponent.
   * Initializes services and the metric model.
   *
   * @param {ToastrService} toastrService - Service for toast notifications.
   * @param {MetricsService} metricsService - Service for fetching metrics data.
   * @param {PondCultureService} pondCultureService - Service for pond culture operations.
   * @param {ActivatedRoute} route - Service for retrieving route information.
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations.
   * @param {FarmSiteService} farmSiteService - Service for farm site operations.
   * @param {CustomerPondService} customerPondService - Service for customer pond operations.
   * @param {PondSectionService} pondSectionService - Service for pond section operations.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   *
   * @service ToastrService
   * @service MetricsService
   * @service PondCultureService
   * @service ActivatedRoute
   * @service BusinessEntityService
   * @service FarmSiteService
   * @service CustomerPondService
   * @service PondSectionService
   * @service LocationStrategy
   * @service PreviousRouteService
   */
  constructor(private toastrService: ToastrService, public metricsService: MetricsService, public pondCultureService: PondCultureService, public route: ActivatedRoute, public businessEntityService: BusinessEntityService, public farmSiteService: FarmSiteService, public customerPondService: CustomerPondService, public pondSectionService: PondSectionService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.metric = new MetricsModel();
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Retrieves user context from `localStorage`.
   * - Initializes date ranges.
   * - Sets up the previous route.
   * - Determines logic flow based on `loginBusinessCategory`:
   *   - **Farm**: Auto-sets business ID and fetches farm sites based on `loginPersonType`.
   *   - **Default**: Fetches all business entities for selection.
   *
   */
  public async ngOnInit() {
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    this.appPermission = this.route.snapshot.data['rolepermission'];
    try {
      this.keyword = 'businessName';
      this.loginPersonType = localStorage.getItem('loginPersonType');
      /* user login details */
      this.LoginUserName = localStorage.getItem('LoginUserName');
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      /* user login details end */
      this.currentDay = (moment(new Date()).format('DD-MMM-YYYY')).toString(); // "2020-05-20"
      if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
        this.farmsites = await this.farmSiteService.getFarmeSitesByFarmId(localStorage.businessID);
        this.pgLoad = true;
        this.setSingleValueForFormLocation();
        this.metric.business_id = localStorage.businessID;
      }
      this.noOfDays = 'seven';
      this.metric.ph = true;
      this.metric.temp = true;
      this.metric.Salinity = true;
      this.metric.ABW = true;
      this.metric.ADG = true;
      this.dayOrCulture = false;
      this.isSearchClicked = false;
      this.addCompare = true;
      this.removeCompare = false;
      this.viewclicked = false;
      this.initDates();
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @lifecycle ngOnDestroy
   * @description Lifecycle hook that is called when the component is destroyed.
   * Cancels ongoing HTTP requests to prevent memory leaks.
   * @side-effect Cancels HTTP requests.
   */
  public ngOnDestroy(): void {
    this.farmSiteService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
  }

  /**
   * @method readingsTab
   * @description Switches the view to the Readings tab.
   * Resets search state and fetches data if farm site is selected.
   */
  public readingsTab() {
    this.isSearchClicked = false;
    this.tabIndex = 0;
    this.viewclicked = false;
    if (this.metric.farm_site_id) {
      this.getDbData();
    }
    if (this.activepondsDetails_dropdown.length > 0) {
      this.metric.pondId = this.activepondsDetails_dropdown[0].pond_id;
    }
  }

  /**
   * @method growthTab
   * @description Switches the view to the Growth tab.
   * Resets search state and fetches data if farm site is selected.
   */
  public growthTab() {
    this.isSearchClicked = false;
    this.viewclicked = false;
    this.tabIndex = 1;
    if (this.metric.farm_site_id) {
      this.getDbData();
    }
    if (this.activepondsDetails_dropdown.length > 0) {
      this.metric.pondId = this.activepondsDetails_dropdown[0].pond_id;
    }
  }

  /**
   * @method compareTab
   * @description Switches the view to the Compare tab.
   * Resets search state and fetches data if farm site is selected.
   */
  public compareTab() {
    this.isSearchClicked = false;
    this.viewclicked = false;
    this.tabIndex = 2;
    if (this.metric.farm_site_id) {
      this.getDbData();
    }
    if (this.activepondsDetails_dropdown.length > 0) {
      this.metric.pondId = this.activepondsDetails_dropdown[0].pond_id;
    }
  }

  /**
   * @method hideallcharts
   * @description Resets all chart display flags to false based on the current tab index.
   * Ensures clean state when switching views or reloading data.
   */
  public hideallcharts() {
    if (this.tabIndex == 0) {
      this.phChartDisp = false;
      this.bicorbonateDisp = false;
      this.uammoniaChartDisp = false;
      this.alkalinityChartDisp = false;
      this.bioflocChartDisp = false;
      this.calciumChartDisp = false;
      this.carbonateChartDisp = false;
      this.doChartDisp = false;
      this.hardnessChartDisp = false;
      this.magnesimChartDisp = false;
      this.nitriteChartDisp = false;
      this.orpChartDisp = false;
      this.salinityChartDisp = false;
      this.turbidityChartDisp = false;
      this.tanChartDisp = false;
      this.tdsChartDisp = false;
      this.tempChartDisp = false;
    }
  }

  /**
   * @method getAllFarmsitesBybusinessId
   * @async
   * @description Fetches farm sites for the selected business ID (Admin/Employee flow).
   */
  public async getAllFarmsitesBybusinessId() {
    this.hideallcharts();
    this.metric.farm_site_id = undefined;
    this.farmsites = [];
    this.metric.section_id = undefined;
    this.activepondsDetails_dropdown = [];
    try {
      this.farmsites = await this.farmSiteService.getFarmeSitesByFarmId(this.metric.business_id);
      this.pgLoad = true;
      this.setSingleValueForFormLocation();
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method setSingleValueForFormLocation
   * @description Auto-selects the farm site if only one exists and triggers section fetching and data loading.
   * @side-effect Updates `metric.farm_site_id`.
   */
  public setSingleValueForFormLocation() {
    if (this.farmsites && this.farmsites.length == 1) {
      this.metric.farm_site_id = this.farmsites[0].id; // model-binding key
      this.getSetionsByFarmsiteId(this.metric.farm_site_id);
      this.getDbData();
    }
  }

  /**
   * @method farmSiteChange
   * @description Handles farm site selection change.
   * Triggers data fetch if farm site is selected.
   */
  public farmSiteChange() {
    if (this.metric.farm_site_id) {
      this.getDbData();
    }
  }

  /**
   * @method getSetionsByFarmsiteId
   * @async
   * @description Fetches sections associated with a specific farm site ID.
   * Auto-selects the section if only one exists.
   * @param {string} farmsiteid - The ID of the farm site.
   */
  public async getSetionsByFarmsiteId(farmsiteid: string) {
    this.hideallcharts();
    this.metric.section_id = undefined;
    this.pondSections = await this.pondSectionService.getSectionsByFarmsiteId(farmsiteid);
    this.setSingleValueForSection();
  }

  /**
   * @method setSingleValueForSection
   * @description Auto-selects the section if only one exists and triggers pond fetching.
   * @side-effect Updates `metric.section_id`.
   */
  public setSingleValueForSection() {
    if (this.pondSections && this.pondSections.length == 1) {
      this.metric.section_id = Number(this.pondSections[0].id); // model-binding key
      this.getPondsBySectionId(this.metric.section_id);
    }
  }

  /**
   * @method getPondsBySectionId
   * @async
   * @description Fetches ponds associated with a specific section ID.
   * Populates `activepondsDetails_dropdown` with active ponds and calculates DOC.
   * @param {number} section_id - The ID of the section.
   */
  public async getPondsBySectionId(section_id: number) {
    this.activepondsDetails_dropdown = [];
    try {
      this.customerPonds = await this.customerPondService.getAllPondsBySectionId(section_id);
      this.activepondsDetails_dropdown = [];
      if (this.customerPonds && this.customerPonds.length > 0) {
        for (let pond = 0; pond < this.customerPonds.length; pond++) {
          if ((this.customerPonds[pond].PondCultureData && this.customerPonds[pond].PondCultureData.length > 0)) {
            let eachpondculture: any = this.customerPonds[pond].PondCultureData[this.customerPonds[pond].PondCultureData.length - 1];
            if (eachpondculture) {
              eachpondculture.pond_name = this.customerPonds[pond].name;
              eachpondculture.pond_id = this.customerPonds[pond].id;
              eachpondculture.isPreparatory = this.customerPonds[pond].isPreparatory;
              this.activepondsDetails_dropdown.push(eachpondculture);
            }
          } else if (this.customerPonds[pond].isPreparatory == true) {
            let isPreObj: any = {
              pond_id: this.customerPonds[pond].id,
              pond_name: this.customerPonds[pond].name,
              isPreparatory: this.customerPonds[pond].isPreparatory,
            };
            this.activepondsDetails_dropdown.push(isPreObj);
          }
        }
        for (let b = 0; b < this.activepondsDetails_dropdown.length; b++) {
          if (this.activepondsDetails_dropdown[b].start_date) {
            let date1 = new Date(this.activepondsDetails_dropdown[b].start_date.split('T')[0]);
            let date2 = new Date();
            let Difference_In_Time = date2.getTime() - date1.getTime();
            let diffDays = Math.ceil(Difference_In_Time / (1000 * 60 * 60 * 24));
            this.activepondsDetails_dropdown[b].doc = diffDays;
          }
        }
        if (this.activepondsDetails_dropdown.length > 0) {
          if (this.tabIndex != 2 && this.viewclicked == false) {
            this.activepondsDetails_dropdown[0].isChecked = true;
            this.checkedIDs.push(this.activepondsDetails_dropdown[0].pond_id);
          } else if (this.tabIndex == 2 && this.viewclicked == false) {
            this.metric.pondId = this.activepondsDetails_dropdown[0].pond_id;
          } /*else if (this.viewclicked == true) {
            this.metric.comparepondId = this.activepondsDetails_dropdown[0].pond_id;
          }*/
        }
        this.prepareChart();
        this.fetchSelectedItems();
      }
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method fetchSelectedItems
   * @description Filters the `activepondsDetails_dropdown` to get currently selected items.
   * Triggers chart preparation.
   */
  public fetchSelectedItems() {
    this.selectedItemsList = this.activepondsDetails_dropdown.filter((value) => {
      return value.isChecked;
    });
    // this.pondName = this.selectedItemsList.map(name => name.pond_name);
    this.prepareChart();
  }

  /**
   * @method prepareChart
   * @description Prepares chart data based on selected ponds and current tab index.
   * Calls `newMethod` to process data or resets chart display flags if no ponds selected.
   */
  public prepareChart() { // parechart() for selected tank id
    this.viewclicked = false;
    if (this.tabIndex != 2) {
      this.checkedIDs = [];
      this.activepondsDetails_dropdown.forEach((value) => {
        if (value.isChecked) {
          this.checkedIDs.push(value.pond_id);
        }
      });
      if (this.checkedIDs.length > 0) {
        this.newMethod();
      } else {
        if (this.tabIndex == 0) {
          this.phChartDisp = false;
          this.bicorbonateDisp = false;
          this.uammoniaChartDisp = false;
          this.alkalinityChartDisp = false;
          this.bioflocChartDisp = false;
          this.calciumChartDisp = false;
          this.carbonateChartDisp = false;
          this.doChartDisp = false;
          this.hardnessChartDisp = false;
          this.magnesimChartDisp = false;
          this.nitriteChartDisp = false;
          this.orpChartDisp = false;
          this.salinityChartDisp = false;
          this.turbidityChartDisp = false;
          this.tdsChartDisp = false;
          this.tanChartDisp = false;
          this.tempChartDisp = false;
        } else if (this.tabIndex == 1) {
          this.abwChartDisp = false;
          this.adgChartDisp = false;
          this.fcrChartDisp = false;
          this.srChartDisp = false;
          this.dcfChartDisp = false;
          this.tbmChartDisp = false;
        }
      }
    } else if (this.tabIndex == 2) {
      if (this.metric.pondId) {
        this.newMethod();
      } else {
        this.compareChartDisplay = false;
      }
    }
  }

  /**
   * @method newMethod
   * @description Main logic for preparing datasets for charts based on `chartGlobalData`.
   * Iterates through selected ponds and parameters to build datasets for Chart.js.
   * Handles data for Readings, Growth, and Compare tabs.
   */
  public newMethod() {
    let readings = this.chartGlobalData; // globaldata
    let labels = _.map(readings, 'date');
    labels = _.uniq(labels); // [2021-09-02, 2021-08-02]
    labels = _.sortBy(labels);
    let phDataSets = [];
    let temperatureDataSets = [];
    let salinityDataSets = [];
    let ammoniaDataSets = [];
    let alkalinityDataSets = [];
    let bioflockDataSets = [];
    let bicarbonateDataSets = [];
    let calciumDataSets = [];
    let carbonateDataSets = [];
    let doDataSets = [];
    let hardnessDataSets = [];
    let magnesiumDataSets = [];
    let nitriteDataSets = [];
    let orpDataSets = [];
    let turbidityDataSets = [];
    let tanDataSets = [];
    let tdsDataSets = [];
    let abwDataSets = [];
    let srDataSets = [];
    let adgDataSets = [];
    let tbmDataSets = [];
    let fcrDataSets = [];
    let dcfDataSets = [];
    let one: any;
    let two: any;
    let tanksData = _.groupBy(this.chartGlobalData, 'pond_id');
    if (this.tabIndex != 2) {
      for (let e = 0; e < this.checkedIDs.length; e++) {
        let tankId = this.checkedIDs[e];
        if (tanksData) {
          let eachTankData = tanksData[tankId];
          /*ph graph data prepare*/
          if (eachTankData) {
            if(this.selectedItemsList && this.selectedItemsList.length > 0){
              for (let s = 0; s < this.selectedItemsList.length; s++) {
                let each = this.selectedItemsList[s];
                for (let h = 0; h < eachTankData.length; h++) {
                  eachTankData[h] = JSON.parse(JSON.stringify(eachTankData[h]));
                  if (eachTankData[h].pond_id == each.pond_id) {
                    eachTankData[h].name = each.pond_name;
                  }
                }
              }
            }
            if (this.tabIndex == 0) {
              let phdataset = this.getDatasetNew(eachTankData, 'pH', labels);
              if (phdataset) {
                phDataSets.push(phdataset);
              }
              /*bicarbonate graph data prepare*/
              let bicarbonateset = this.getDatasetNew(eachTankData, 'bicarbonate', labels);
              if (bicarbonateset) {
                bicarbonateDataSets.push(bicarbonateset);
              }
              let tempdataset = this.getDatasetNew(eachTankData, 'temperature', labels);
              if (tempdataset) {
                temperatureDataSets.push(tempdataset);
              }
              let salinityset = this.getDatasetNew(eachTankData, 'salinity', labels);
              if (salinityset) {
                salinityDataSets.push(salinityset);
              }
              let alkalinityset = this.getDatasetNew(eachTankData, 'alkalinity', labels);
              if (alkalinityset) {
                alkalinityDataSets.push(alkalinityset);
              }
              let ammoniaset = this.getDatasetNew(eachTankData, 'uam', labels);
              if (ammoniaset) {
                ammoniaDataSets.push(ammoniaset);
              }
              let bioflocset = this.getDatasetNew(eachTankData, 'biofloc', labels);
              if (bioflocset) {
                bioflockDataSets.push(bioflocset);
              }
              let calciumset = this.getDatasetNew(eachTankData, 'calcium', labels);
              if (calciumset) {
                calciumDataSets.push(calciumset);
              }
              let carbonateset = this.getDatasetNew(eachTankData, 'carbonates', labels);
              if (carbonateset) {
                carbonateDataSets.push(carbonateset);
              }
              let doset = this.getDatasetNew(eachTankData, 'do2', labels);
              if (doset) {
                doDataSets.push(doset);
              }
              let hardnessset = this.getDatasetNew(eachTankData, 'hardness', labels);
              if (hardnessset) {
                hardnessDataSets.push(hardnessset);
              }
              let magnesiumset = this.getDatasetNew(eachTankData, 'magnesium', labels);
              if (magnesiumset) {
                magnesiumDataSets.push(magnesiumset);
              }
              let nitriteset = this.getDatasetNew(eachTankData, 'nitrite', labels);
              if (nitriteset) {
                nitriteDataSets.push(nitriteset);
              }
              let orpset = this.getDatasetNew(eachTankData, 'orp', labels);
              if (orpset) {
                orpDataSets.push(orpset);
              }
              let turbidityset = this.getDatasetNew(eachTankData, 'turbidity', labels);
              if (turbidityset) {
                turbidityDataSets.push(turbidityset);
              }
              let tanset = this.getDatasetNew(eachTankData, 'tan', labels);
              if (tanset) {
                tanDataSets.push(tanset);
              }
              let tdsset = this.getDatasetNew(eachTankData, 'tds', labels);
              if (tdsset) {
                tdsDataSets.push(tdsset);
              }
            }
            /*growth graph*/
            if (this.tabIndex == 1) {
              /*abw*/
              let abwdataset = this.getDatasetNew(eachTankData, 'abw', labels);
              if (abwdataset) {
                abwDataSets.push(abwdataset);
              }
              /*adg*/
              let adgdataset = this.getDatasetNew(eachTankData, 'adg', labels);
              if (adgdataset) {
                adgDataSets.push(adgdataset);
              }
              /*fcr*/
              let fcrdataset = this.getDatasetNew(eachTankData, 'FCR', labels);
              if (fcrdataset) {
                fcrDataSets.push(fcrdataset);
              }
              /*total Biomass*/
              let tbmdataset = this.getDatasetNew(eachTankData, 'total_biomass', labels);
              if (tbmdataset) {
                tbmDataSets.push(tbmdataset);
              }
              /*sr*/
              let srdataset = this.getDatasetNew(eachTankData, 'survival_rate', labels);
              if (srdataset) {
                srDataSets.push(srdataset);
              }
              /*day cumulative feed*/
              let dcfdataset = this.getDatasetNew(eachTankData, 'cumulative_feed', labels);
              if (dcfdataset) {
                dcfDataSets.push(dcfdataset);
              }
            }
          }
        }
      }
    } else if (this.tabIndex == 2) {
      let tankId = this.metric.pondId;
      if (tanksData) {
        let eachTankData = tanksData[tankId];
        /*campare graph*/
        if (eachTankData) {
          one = this.getDatasetNew(eachTankData, this.readingParam, labels);
          two = this.getDatasetNew(eachTankData, this.growthParam, labels);
        }
      }
    }
    if (this.tabIndex == 0) {
      if (phDataSets && phDataSets.length > 0) {
        let oroginalPhdataSet = [];
        for (let y = 0; y < phDataSets.length; y++) {
          let data = _.cloneDeep((phDataSets[y] as any)?.data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginalPhdataSet.push(phDataSets[y]);
          }
        }
        if (oroginalPhdataSet && oroginalPhdataSet.length > 0) {
          this.phChartDisp = true;
          this.pHChart(oroginalPhdataSet, labels);
        } else {
          this.phChartDisp = false;
        }
      }
      if (bicarbonateDataSets && bicarbonateDataSets.length > 0) {
        let oroginalbicarbonatedataSet = [];
        for (let y = 0; y < bicarbonateDataSets.length; y++) {
          let data = _.cloneDeep((bicarbonateDataSets[y] as any)?.data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginalbicarbonatedataSet.push(bicarbonateDataSets[y]);
          }
        }
        if (oroginalbicarbonatedataSet && oroginalbicarbonatedataSet.length > 0) {
          this.bicorbonateDisp = true;
          this.bicarbonateChart(oroginalbicarbonatedataSet, labels);
        } else {
          this.bicorbonateDisp = false;
        }
      }
      if (temperatureDataSets && temperatureDataSets.length > 0) {
        let oroginaltemperaturedataSet = [];
        for (let y = 0; y < temperatureDataSets.length; y++) {
          let data = _.cloneDeep((temperatureDataSets[y] as any)?.data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginaltemperaturedataSet.push(temperatureDataSets[y]);
          }
        }
        if (oroginaltemperaturedataSet && oroginaltemperaturedataSet.length > 0) {
          this.tempChartDisp = true;
          this.tempChart(oroginaltemperaturedataSet, labels);
        } else {
          this.tempChartDisp = false;
        }
      }
      if (salinityDataSets && salinityDataSets.length > 0) {
        let oroginalsalinitydataSet = [];
        for (let y = 0; y < salinityDataSets.length; y++) {
          let data = _.cloneDeep((salinityDataSets[y] as any)?.data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginalsalinitydataSet.push(salinityDataSets[y]);
          }
        }
        if (oroginalsalinitydataSet && oroginalsalinitydataSet.length > 0) {
          this.salinityChartDisp = true;
          this.salinityChart(oroginalsalinitydataSet, labels);
        } else {
          this.salinityChartDisp = false;
        }
      }
      if (ammoniaDataSets && ammoniaDataSets.length > 0) {
        let oroginalammoniadataSet = [];
        for (let y = 0; y < ammoniaDataSets.length; y++) {
          let data = _.cloneDeep((ammoniaDataSets[y] as any)?.data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginalammoniadataSet.push(ammoniaDataSets[y]);
          }
        }
        if (oroginalammoniadataSet && oroginalammoniadataSet.length > 0) {
          this.uammoniaChartDisp = true;
          this.ammoniaChart(oroginalammoniadataSet, labels);
        } else {
          this.uammoniaChartDisp = false;
        }
      }
      if (alkalinityDataSets && alkalinityDataSets.length > 0) {
        let oroginalalkalinitydataSet = [];
        for (let y = 0; y < alkalinityDataSets.length; y++) {
          let data = _.cloneDeep((alkalinityDataSets[y] as any)?.data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginalalkalinitydataSet.push(alkalinityDataSets[y]);
          }
        }
        if (oroginalalkalinitydataSet && oroginalalkalinitydataSet.length > 0) {
          this.alkalinityChartDisp = true;
          this.alkalinityChart(oroginalalkalinitydataSet, labels);
        } else {
          this.alkalinityChartDisp = false;
        }
      }
      if (bioflockDataSets && bioflockDataSets.length > 0) {
        let oroginalbioflocdataSet = [];
        for (let y = 0; y < bioflockDataSets.length; y++) {
          let data = _.cloneDeep((bioflockDataSets[y] as any)?.data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginalbioflocdataSet.push(bioflockDataSets[y]);
          }
        }
        if (oroginalbioflocdataSet && oroginalbioflocdataSet.length > 0) {
          this.bioflocChartDisp = true;
          this.bioflocChart(oroginalbioflocdataSet, labels);
        } else {
          this.bioflocChartDisp = false;
        }
      }
      if (calciumDataSets && calciumDataSets.length > 0) {
        let oroginalcalciumdataSet = [];
        for (let y = 0; y < calciumDataSets.length; y++) {
          let data = _.cloneDeep((calciumDataSets[y] as any)?.data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginalcalciumdataSet.push(calciumDataSets[y]);
          }
        }
        if (oroginalcalciumdataSet && oroginalcalciumdataSet.length > 0) {
          this.calciumChartDisp = true;
          this.calciumChart(oroginalcalciumdataSet, labels);
        } else {
          this.calciumChartDisp = false;
        }
      }
      if (carbonateDataSets && carbonateDataSets.length > 0) {
        let oroginalcarbonatedataSet = [];
        for (let y = 0; y < carbonateDataSets.length; y++) {
          let data = _.cloneDeep((carbonateDataSets[y] as any)?.data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginalcarbonatedataSet.push(carbonateDataSets[y]);
          }
        }
        if (oroginalcarbonatedataSet && oroginalcarbonatedataSet.length > 0) {
          this.carbonateChartDisp = true;
          this.carbonateChart(oroginalcarbonatedataSet, labels);
        } else {
          this.carbonateChartDisp = false;
        }
      }
      if (doDataSets && doDataSets.length > 0) {
        let oroginalDodataSet = [];
        for (let x = 0; x < doDataSets.length; x++) {
          let data = _.cloneDeep((doDataSets[x] as any)?.data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginalDodataSet.push(doDataSets[x]);
          }
        }
        if (oroginalDodataSet && oroginalDodataSet.length > 0) {
          this.doChartDisp = true;
          this.doChart(oroginalDodataSet, labels);
        } else {
          this.doChartDisp = false;
        }
      }
      if (hardnessDataSets && hardnessDataSets.length > 0) {
        let oroginalhardnessdataSet = [];
        for (let x = 0; x < hardnessDataSets.length; x++) {
          let data = _.cloneDeep((hardnessDataSets[x] as any)?.data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginalhardnessdataSet.push(hardnessDataSets[x]);
          }
        }
        if (oroginalhardnessdataSet && oroginalhardnessdataSet.length > 0) {
          this.hardnessChartDisp = true;
          this.hardnessChart(oroginalhardnessdataSet, labels);
        } else {
          this.hardnessChartDisp = false;
        }
      }
      if (magnesiumDataSets && magnesiumDataSets.length > 0) {
        let oroginalmagnesiumdataSet = [];
        for (let x = 0; x < magnesiumDataSets.length; x++) {
          let data = _.cloneDeep((magnesiumDataSets[x] as any)?.data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginalmagnesiumdataSet.push(magnesiumDataSets[x]);
          }
        }
        if (oroginalmagnesiumdataSet && oroginalmagnesiumdataSet.length > 0) {
          this.magnesimChartDisp = true;
          this.magnesiumChart(oroginalmagnesiumdataSet, labels);
        } else {
          this.magnesimChartDisp = false;
        }
      }
      if (nitriteDataSets && nitriteDataSets.length > 0) {
        let oroginalNitritedataSet = [];
        for (let x = 0; x < nitriteDataSets.length; x++) {
          let data = _.cloneDeep((nitriteDataSets[x] as any)?.data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginalNitritedataSet.push(nitriteDataSets[x]);
          }
        }
        if (oroginalNitritedataSet && oroginalNitritedataSet.length > 0) {
          this.nitriteChartDisp = true;
          this.nitriteChart(oroginalNitritedataSet, labels);
        } else {
          this.nitriteChartDisp = false;
        }
      }
      if (orpDataSets && orpDataSets.length > 0) {
        let oroginalorpdataSet = [];
        for (let x = 0; x < orpDataSets.length; x++) {
          let data = _.cloneDeep((orpDataSets[x] as any)?.data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginalorpdataSet.push(orpDataSets[x]);
          }
        }
        if (oroginalorpdataSet && oroginalorpdataSet.length > 0) {
          this.orpChartDisp = true;
          this.orpChart(oroginalorpdataSet, labels);
        } else {
          this.orpChartDisp = false;
        }
      }
      if (turbidityDataSets && turbidityDataSets.length > 0) {
        let oroginalturbiditydataSet = [];
        for (let x = 0; x < turbidityDataSets.length; x++) {
          let data = _.cloneDeep((turbidityDataSets[x] as any)?.data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginalturbiditydataSet.push(turbidityDataSets[x]);
          }
        }
        if (oroginalturbiditydataSet && oroginalturbiditydataSet.length > 0) {
          this.turbidityChartDisp = true;
          this.turbidityChart(oroginalturbiditydataSet, labels);
        } else {
          this.turbidityChartDisp = false;
        }
      }
      if (tanDataSets && tanDataSets.length > 0) {
        let oroginaltandataSet = [];
        for (let x = 0; x < tanDataSets.length; x++) {
          let data = _.cloneDeep((tanDataSets[x] as any)?.data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginaltandataSet.push(tanDataSets[x]);
          }
        }
        if (oroginaltandataSet && oroginaltandataSet.length > 0) {
          this.tanChartDisp = true;
          this.tanChart(oroginaltandataSet, labels);
        } else {
          this.tanChartDisp = false;
        }
      }
      if (tdsDataSets && tdsDataSets.length > 0) {
        let oroginaltdsdataSet = [];
        for (let x = 0; x < tdsDataSets.length; x++) {
          let data = _.cloneDeep((tdsDataSets[x] as any).data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginaltdsdataSet.push(tdsDataSets[x]);
          }
        }
        if (oroginaltdsdataSet && oroginaltdsdataSet.length > 0) {
          this.tdsChartDisp = true;
          this.tdsChart(oroginaltdsdataSet, labels);
        } else {
          this.tdsChartDisp = false;
        }
      }
    }
    /*growth graph data*/
    if (this.tabIndex == 1) {
      if (abwDataSets && abwDataSets.length > 0) {
        let oroginalabwdataSet = [];
        for (let y = 0; y < abwDataSets.length; y++) {
          let data = _.cloneDeep((abwDataSets[y] as any).data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginalabwdataSet.push(abwDataSets[y]);
          }
        }
        if (oroginalabwdataSet && oroginalabwdataSet.length > 0) {
          this.abwChartDisp = true;
          this.abwChart(oroginalabwdataSet, labels);
        } else {
          this.abwChartDisp = false;
        }
      }
      /*adgdaset prepare*/
      if (adgDataSets && adgDataSets.length > 0) {
        let oroginaladgdataSet = [];
        for (let y = 0; y < adgDataSets.length; y++) {
          let data = _.cloneDeep((adgDataSets[y] as any)?.data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginaladgdataSet.push(adgDataSets[y]);
          }
        }
        if (oroginaladgdataSet && oroginaladgdataSet.length > 0) {
          this.adgChartDisp = true;
          this.adgChart(oroginaladgdataSet, labels);
        } else {
          this.adgChartDisp = false;
        }
      }
      /*fcrdaset prepare*/
      if (fcrDataSets && fcrDataSets.length > 0) {
        let oroginalfcrdataSet = [];
        for (let y = 0; y < fcrDataSets.length; y++) {
          let data = _.cloneDeep((fcrDataSets[y] as any)?.data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginalfcrdataSet.push(fcrDataSets[y]);
          }
        }
        if (oroginalfcrdataSet && oroginalfcrdataSet.length > 0) {
          this.fcrChartDisp = true;
          this.fcrChart(oroginalfcrdataSet, labels);
        } else {
          this.fcrChartDisp = false;
        }
      }
      /*total biomassdaset prepare*/
      if (tbmDataSets && tbmDataSets.length > 0) {
        let oroginaltbmdataSet = [];
        for (let y = 0; y < tbmDataSets.length; y++) {
          let data = _.cloneDeep((tbmDataSets[y] as any)?.data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginaltbmdataSet.push(tbmDataSets[y]);
          }
        }
        if (oroginaltbmdataSet && oroginaltbmdataSet.length > 0) {
          this.tbmChartDisp = true;
          this.tbmChart(oroginaltbmdataSet, labels);
        } else {
          this.tbmChartDisp = false;
        }
      }
      /*sr*/
      if (srDataSets && srDataSets.length > 0) {
        let oroginalsrdataSet = [];
        for (let y = 0; y < srDataSets.length; y++) {
          let data = _.cloneDeep((srDataSets[y] as any)?.data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginalsrdataSet.push(srDataSets[y]);
          }
        }
        if (oroginalsrdataSet && oroginalsrdataSet.length > 0) {
          this.srChartDisp = true;
          this.srChart(oroginalsrdataSet, labels);
        } else {
          this.srChartDisp = false;
        }
      }
      /*cumulative feed*/
      if (dcfDataSets && dcfDataSets.length > 0) {
        let oroginaldcfdataSet = [];
        for (let y = 0; y < dcfDataSets.length; y++) {
          let data = _.cloneDeep((dcfDataSets[y] as any)?.data);
          _.remove(data, function(n) {
            return n == -1 || n == null;
          });
          if (data && data.length > 0) {
            oroginaldcfdataSet.push(dcfDataSets[y]);
          }
        }
        if (oroginaldcfdataSet && oroginaldcfdataSet.length > 0) {
          this.dcfChartDisp = true;
          this.dcfChart(oroginaldcfdataSet, labels);
        } else {
          this.dcfChartDisp = false;
        }
      }
    }
    if (this.tabIndex == 2) {
      if (one || two) {
        this.compareChartDisplay = true;
        this.compareChart(one, two, labels);
      } else {
        this.compareChartDisplay = false;
      }
    }
  }

  /**
   * @method getDatasetNew
   * @description Helper method to extract data for a specific parameter and tank from the dataset.
   * Formats data for Chart.js dataset structure.
   * @param {any[]} eachTankData - Data for a specific tank.
   * @param {string} type - The parameter key (e.g., 'pH', 'abw').
   * @param {string[]} timelabels - Array of date labels.
   */
  public getDatasetNew(eachTankData: any, type: any, timelabels: any) {
    // START LOGIC FOR DATA PREPARATION
    let data = [];
    for (let index = 0; index < timelabels.length; index++) {
      let temp = [];
      for (let eachtank = 0; eachtank < eachTankData.length; eachtank++) {
        let typevalue = eachTankData[eachtank][type]; // ex: ph value
        if (eachTankData[eachtank]['date'] == timelabels[index]) {
          if (typevalue == -1 || typevalue == 'null' || typevalue == undefined || typevalue == 'Infinity' || typevalue == 'NaN') {
            typevalue = null;
          }
          temp.push(typevalue);
        }
      }
      _.remove(temp, function(n) {
        return n == -1 || n == null || n == 'Infinity' || n == 'NaN';
      });
      // preapare data for each label
      if (temp && temp.length > 0) { // [7,8,9]
        data.push(temp);
      } else { // for that date if tank doesnot contain any value
        data.push(null);
      }
    }
    // this.checkedIDs//[9,8]
    /*for (let s = 0; s < this.selectedItemsList.length; s++) {
      let each = this.selectedItemsList[s];
      if (eachTankData[0].pond_id == each.pond_id) {
        eachTankData[0].name = each.pond_name
      }
    }
*/
    // END LOGIC FOR DATA PREPARATION
    if (this.tabIndex != 2 && this.viewclicked != true) {
      // let pondName = this.selectedItemsList.map(name => name.pond_name);
      if (data && data.length > 0) {
        let dataset = {
          data: data,
          label: eachTankData[0].name,
          // label: pondName,
          tankId: eachTankData && eachTankData.length > 0 && eachTankData[0].pond_id || '',
          borderColor: this.getRandomColor(),
          fill: false,
          spanGaps: true,
        };
        return dataset;
      }
      return '';
    } else if (this.tabIndex == 2 || this.viewclicked == true) {
      if (data && data.length > 0) {
        let dataset = data;
        return dataset;
      }
      return '';
    }
  }

  /**
   * @method initDates
   * @description Initializes date range variables (7 days, 30 days, 100 days ago).
   */
  public initDates() {
    let date = new Date();
    date.setDate(date.getDate() - 7);
    this.sevenfromDate = date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
    date = new Date();
    date.setDate(date.getDate() - 30);
    this.thirtyfromDate = date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
    date = new Date();
    date.setDate(date.getDate() - 100);
    this.hundreadfromDate = date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
    date = new Date();
    this.fromDate = date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
  }

  /**
   * @method selectedDateReading
   * @description Handles change in date range selection.
   * Triggers `prepareGlobalData`.
   * @param {string} val - Selected date range value.
   */
  public selectedDateReading(val: any) {
    this.noOfDays = val;
    this.prepareGlobalData();
  }

  /**
   * @method checkAllData
   * @description Toggles all reading parameters based on the 'all' checkbox state.
   */
  public checkAllData() {
    if (this.metric.all == true) {
      this.metric.ph = true;
      this.metric.custalkalinity = true;
      this.metric.bicorbonate = true;
      this.metric.biofloc = true;
      this.metric.custcalcium = true;
      this.metric.Carbonate = true;
      this.metric.do = true;
      this.metric.custhardness = true;
      this.metric.magnisium = true;
      this.metric.custNitrite = true;
      this.metric.Orp = true;
      this.metric.Tds = true;
      this.metric.Tan = true;
      this.metric.Turbidity = true;
      this.metric.Uammonia = true;
      this.metric.Salinity = true;
      this.metric.temp = true;
    } else {
      this.metric.ph = false;
      this.metric.custalkalinity = false;
      this.metric.bicorbonate = false;
      this.metric.biofloc = false;
      this.metric.custcalcium = false;
      this.metric.Carbonate = false;
      this.metric.do = false;
      this.metric.custhardness = false;
      this.metric.magnisium = false;
      this.metric.custNitrite = false;
      this.metric.Orp = false;
      this.metric.Tds = false;
      this.metric.Tan = false;
      this.metric.Turbidity = false;
      this.metric.Salinity = false;
      this.metric.Uammonia = false;
      this.metric.temp = false;
    }
  }

  /**
   * @method growthcheckAllData
   * @description Toggles all growth parameters based on the 'growthAll' checkbox state.
   */
  public growthcheckAllData() {
    if (this.metric.growthAll == true) {
      this.metric.ABW = false;
      this.metric.ADG = false;
      this.metric.fcr = false;
      this.metric.tbm = false;
      this.metric.dcf = false;
      this.metric.sr = false;
    } else {
      this.metric.ABW = true;
      this.metric.ADG = true;
      this.metric.fcr = true;
      this.metric.tbm = true;
      this.metric.dcf = true;
      this.metric.sr = true;
    }
  }

  /**
   * @method uncheckParam
   * @description Unchecks the 'all' checkbox if any individual parameter is unchecked.
   */
  public uncheckParam() {
    if (this.tabIndex == 0) {
      if (this.metric.ph == false || this.metric.temp == false || this.metric.Uammonia == false || this.metric.Salinity == false || this.metric.Turbidity == false || this.metric.Tan == false || this.metric.Tds == false || this.metric.Orp == false || this.metric.custNitrite == false || this.metric.magnisium == false || this.metric.custhardness == false || this.metric.custalkalinity == false || this.metric.bicorbonate == false || this.metric.biofloc == false || this.metric.custcalcium == false || this.metric.Carbonate == false || this.metric.do == false) {
        this.metric.all = false;
      }
    } else if (this.tabIndex == 1) {
      if (this.metric.ABW == false || this.metric.ADG == false || this.metric.sr == false || this.metric.tbm == false || this.metric.fcr == false || this.metric.dcf == false) {
        this.metric.all = false;
      }
    }
  }

  /**
   * @method closeParams
   * @description Closes a specific chart/parameter display.
   * @param {string} param - The parameter to close.
   */
  public closeParams(param: any) {
    if (this.tabIndex == 0) {
      if (param == 'ph') {
        this.metric.ph = false;
      } else if (param == 'ammonia') {
        this.metric.Uammonia = false;
      } else if (param == 'alkalinity') {
        this.metric.custalkalinity = false;
      } else if (param == 'calcium') {
        this.metric.custcalcium = false;
      } else if (param == 'temp') {
        this.metric.temp = false;
      } else if (param == 'do') {
        this.metric.do = false;
      } else if (param == 'salinity') {
        this.metric.Salinity = false;
      } else if (param == 'biofloc') {
        this.metric.biofloc = false;
      } else if (param == 'carbonate') {
        this.metric.Carbonate = false;
      } else if (param == 'hardness') {
        this.metric.custhardness = false;
      } else if (param == 'magnesium') {
        this.metric.magnisium = false;
      } else if (param == 'nitrite') {
        this.metric.custNitrite = false;
      } else if (param == 'orp') {
        this.metric.Orp = false;
      } else if (param == 'turbidity') {
        this.metric.Turbidity = false;
      } else if (param == 'tan') {
        this.metric.Tan = false;
      } else if (param == 'tds') {
        this.metric.Tds = false; // bicarbonate
      } else if (param == 'bicarbonate') {
        this.metric.bicorbonate = false;
      }
    } else if (this.tabIndex == 1) {
      if (param == 'abw') {
        this.metric.ABW = false;
      } else if (param == 'adg') {
        this.metric.ADG = false;
      } else if (param == 'fcr') {
        this.metric.fcr = false;
      } else if (param == 'tbm') {
        this.metric.tbm = false;
      } else if (param == 'sr') {
        this.metric.sr = false;
      } else if (param == 'dcf') {
        this.metric.dcf = false;
      }
    } else if (param == 'compare') {
      this.compareChartDisplay = false;
    }
  }

  /**
   * @method getDbData
   * @async
   * @description Fetches metrics data from the server for the selected farm site.
   * Processes numeric values for display.
   */
  public async getDbData() {
    this.chartGlobalData = [];
    this.popupchartGlobalData = [];
    this.pgLoad = false;
    this.isSearchClicked = false;
    this.metrics = await this.metricsService.getMetricsDataGraph(this.metric.farm_site_id);
    this.pgLoad = true;
    this.isSearchClicked = true;
    if (this.metrics.length > 0) {
      for (let i = 0; i < this.metrics.length; i++) {
        if (this.metrics[i].total_biomass) {
          this.metrics[i].total_biomass = this.metrics[i].total_biomass / 1000;
          this.metrics[i].total_biomass = Number(this.metrics[i].total_biomass.toString().match(/^\d+(?:\.\d{0,4})?/));
        }
        if (this.metrics[i].cumulative_feed) {
          this.metrics[i].cumulative_feed = this.metrics[i].cumulative_feed / 1000;
          this.metrics[i].cumulative_feed = Number(this.metrics[i].cumulative_feed.toString().match(/^\d+(?:\.\d{0,4})?/));
        }
        if (this.metrics[i].abw) {
          this.metrics[i].abw = Number(this.metrics[i].abw.toString().match(/^\d+(?:\.\d{0,5})?/));
        }
        if (this.metrics[i].adg) {
          this.metrics[i].adg = Number(this.metrics[i].adg.toString().match(/^\d+(?:\.\d{0,5})?/));
        }
        if (this.metrics[i].FCR) {
          this.metrics[i].FCR = Number(this.metrics[i].FCR.toString().match(/^\d+(?:\.\d{0,4})?/));
        }
        if (this.metrics[i].survival_rate) {
          this.metrics[i].survival_rate = Number(this.metrics[i].survival_rate.toString().match(/^\d+(?:\.\d{0,4})?/));
        }
      }
    } else {
      this.toastrService.error('Data not available', '', {
        timeOut: 2000
      });
    }
    this.prepareGlobalData();
  }

  /**
   * @method prepareGlobalData
   * @async
   * @description Filters the fetched metrics data based on the selected date range (`noOfDays`).
   * Handles logic for 'Day' vs 'Culture' cycle filtering.
   */
  public async prepareGlobalData() {
    try {
      this.filteredData = [];
      this.chartGlobalData = [];
      this.popupchartGlobalData = [];
      let toDate = (moment(new Date()).format('YYYY-MM-DD')).toString();
      if (this.noOfDays == 'seven' && this.metrics) {
        let startDate = new Date(this.sevenfromDate);
        let endDate = new Date(toDate);
        this.filteredData = this.metrics.filter(a => {
          let date = new Date(a.date);
          return (date >= startDate && date <= endDate);
        });
      } else if (this.noOfDays == 'thirty' && this.metrics) {
        let startDate = new Date(this.thirtyfromDate);
        let endDate = new Date(toDate);
        this.filteredData = this.metrics.filter(a => {
          let date = new Date(a.date);
          return (date >= startDate && date <= endDate);
        });
      } else if (this.noOfDays == 'hundred' && this.metrics) {
        let startDate = new Date(this.hundreadfromDate);
        let endDate = new Date(toDate);
        this.filteredData = this.metrics.filter(a => {
          let date = new Date(a.date);
          return (date >= startDate && date <= endDate);
        });
      } else if (this.noOfDays == 'today' && this.metrics) {
        let startDate = new Date(this.fromDate);
        let endDate = new Date(toDate);
        this.filteredData = this.resultantReadings.filter(a => {
          let date = new Date(a.date);
          return (date >= startDate && date <= endDate);
        });
      }
      if (this.dayOrCulture == true) {
        for (let k = 0; k < this.filteredData.length; k++) {
          let eachobj = this.filteredData[k];
          this.pondCUltures = await this.pondCultureService.getCulturesByPondId(this.filteredData[k].pond_id);
          for (let b = 0; b < this.pondCUltures.length; b++) {
            let eachpondCultureObj = this.pondCUltures[b];
            if (eachobj.end_date == null) {
              eachobj.pondCultureData = eachpondCultureObj;
              this.chartGlobalData.push(eachobj);
              this.popupchartGlobalData.push(eachobj);
            }
          }
          /*if (eachobj.pondCulture != null) {
            if (eachobj.pondCulture.end_date == null) {
              this.chartGlobalData.push(eachobj);
              this.popupchartGlobalData.push(eachobj);
            }
          }*/
        }
      } else {
        this.chartGlobalData = this.filteredData;
        this.popupchartGlobalData = this.filteredData;
      }
      this.prepareChart();
      if (this.viewclicked == true) {
        this.comparePrepareChart();
      }
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method pHChart
   * @description Renders the pH line chart using Chart.js.
   * Creates a canvas element dynamically and initializes the chart.
   * @param {any[]} phDataSets - Datasets for pH.
   * @param {string[]} label - X-axis labels (DOC).
   */
  public pHChart(phDataSets: any, label: any) {
    $('#phCanvasID').remove(); // this is my <canvas> element
    $('#phCanvas-container').append('<canvas id="phCanvasID"><canvas>');
    let canvas = document.getElementById('phCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.phChartModalData = {
      type: 'line',
      data: {
        labels: label,
        datasets: phDataSets,
      },
      options: {
        // legend: {display: false},
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'pH',
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'pH');
            }
          }
        },
      }
    };
    let chart = new Chart(ctx, this.phChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.phChartModalData);
  }

  /**
   * @method newChart
   * @description Renders a chart in a modal popup based on the selected type.
   * @param {string} type - The type of chart to render (e.g., 'ph', 'temp').
   */
  public newChart(type: string) {
    let data: any = null;
    if (type == 'ph') {
      data = this.phChartModalData;
    }
    if (type == 'temp') {
      data = this.tempChartModalData;
    }
    if (type == 'salinity') {
      data = this.salinityChartModalData;
    }
    if (type == 'ammonia') {
      data = this.ammoniaChartModalData;
    }
    if (type == 'do') {
      data = this.doChartModalData;
    }
    if (type == 'alkalinity') {
      data = this.alkalinityChartModalData;
    }
    if (type == 'biofloc') {
      data = this.bioflocChartModalData;
    }
    if (type == 'calcium') {
      data = this.calciumChartModalData;
    }
    if (type == 'carbonate') {
      data = this.carbonateChartModalData;
    }
    if (type == 'hardness') {
      data = this.hardnessChartModalData;
    }
    if (type == 'magnesium') {
      data = this.magnesiumChartModalData;
    }
    if (type == 'nitrite') {
      data = this.nitriteChartModalData;
    }
    if (type == 'tan') {
      data = this.tanChartModalData;
    }
    if (type == 'tds') {
      data = this.tdsChartModalData;
    }
    if (type == 'orp') {
      data = this.orpChartModalData;
    }
    if (type == 'turbidity') {
      data = this.turbidityChartModalData;
    }
    if (type == 'bicarbonate') {
      data = this.biCarbonateChartModalData;
    }
    if (type == 'abw') {
      data = this.abwChartModalData;
    }
    if (type == 'adg') {
      data = this.adgChartModalData;
    }
    if (type == 'survival_rate') {
      data = this.srChartModalData;
    }
    if (type == 'fcr') {
      data = this.fcrChartModalData;
    }
    if (type == 'dcf') {
      data = this.dcfChartModalData;
    }
    if (type == 'tbm') {
      data = this.tbmChartModalData;
    }
    let modalData = _.cloneDeep(data);
    /*ph canvas modal popup logic*/
    $('#modalCanvasID').remove(); // this is my <canvas> element
    $('#modalCanvas-container').append('<canvas id="modalCanvasID"><canvas>');
    let canvasModal = document.getElementById('modalCanvasID') as HTMLCanvasElement;
    let modalctx = canvasModal.getContext('2d');
    let chart = new Chart(modalctx, modalData);
    chart.destroy();
    chart = new Chart(modalctx, modalData);
    /*ph canvas modal popup logic end*/
    /*temp canvas modal popup logic*/
    $('#tempModalCanvasID').remove(); // this is my <canvas> element
    $('#tempModalCanvas-container').append('<canvas id="tempModalCanvasID"><canvas>');
    let tempCanvasModal = document.getElementById('tempModalCanvasID') as HTMLCanvasElement;
    let tempModalCtx = tempCanvasModal.getContext('2d');
    let tempChart = new Chart(tempModalCtx, modalData);
    tempChart.destroy();
    tempChart = new Chart(tempModalCtx, modalData);
    /*temp canvas modal popup logic end*/
    /*salinity canvas modal popup logic*/
    $('#salinityModalCanvasID').remove(); // this is my <canvas> element
    $('#salinityModalCanvas-container').append('<canvas id="salinityModalCanvasID"><canvas>');
    let salinityCanvasModal = document.getElementById('salinityModalCanvasID') as HTMLCanvasElement;
    let salinityModalCtx = salinityCanvasModal.getContext('2d');
    let salinityChart = new Chart(salinityModalCtx, modalData);
    salinityChart.destroy();
    salinityChart = new Chart(salinityModalCtx, modalData);
    /*salinity canvas modal popup logic end*/
    /*ammonia canvas modal popup logic*/
    $('#ammoniaModalCanvasID').remove(); // this is my <canvas> element
    $('#ammoniaModalCanvas-container').append('<canvas id="ammoniaModalCanvasID"><canvas>');
    let ammoniaCanvasModal = document.getElementById('ammoniaModalCanvasID') as HTMLCanvasElement;
    let ammoniaModalCtx = ammoniaCanvasModal.getContext('2d');
    let ammoniaChart = new Chart(ammoniaModalCtx, modalData);
    ammoniaChart.destroy();
    ammoniaChart = new Chart(ammoniaModalCtx, modalData);
    /*ammonia canvas modal popup logic end*/
    /*do canvas modal popup logic*/
    $('#doModalCanvasID').remove(); // this is my <canvas> element
    $('#doModalCanvas-container').append('<canvas id="doModalCanvasID"><canvas>');
    let doCanvasModal = document.getElementById('doModalCanvasID') as HTMLCanvasElement;
    let doModalCtx = doCanvasModal.getContext('2d');
    let doChart = new Chart(doModalCtx, modalData);
    doChart.destroy();
    doChart = new Chart(doModalCtx, modalData);
    /*do2 canvas modal popup logic end*/
    /*alkalinity canvas modal popup logic*/
    $('#alkalinityModalCanvasID').remove(); // this is my <canvas> element
    $('#alkalinityModalCanvas-container').append('<canvas id="alkalinityModalCanvasID"><canvas>');
    let alkalinityCanvasModal = document.getElementById('alkalinityModalCanvasID') as HTMLCanvasElement;
    let alkalinityModalCtx = alkalinityCanvasModal.getContext('2d');
    let alkalinityChart = new Chart(alkalinityModalCtx, modalData);
    alkalinityChart.destroy();
    alkalinityChart = new Chart(alkalinityModalCtx, modalData);
    /*alkalinity canvas modal popup logic end*/
    /*biofloc canvas modal popup logic*/
    $('#bioflocModalCanvasID').remove(); // this is my <canvas> element
    $('#bioflocModalCanvas-container').append('<canvas id="bioflocModalCanvasID"><canvas>');
    let bioflocCanvasModal = document.getElementById('bioflocModalCanvasID') as HTMLCanvasElement;
    let bioflocModalCtx = bioflocCanvasModal.getContext('2d');
    let bioflocChart = new Chart(bioflocModalCtx, modalData);
    bioflocChart.destroy();
    bioflocChart = new Chart(bioflocModalCtx, modalData);
    /*biofloc canvas modal popup logic end*/
    /*calcium canvas modal popup logic*/
    $('#calciumModalCanvasID').remove(); // this is my <canvas> element
    $('#calciumModalCanvas-container').append('<canvas id="calciumModalCanvasID"><canvas>');
    let calciumCanvasModal = document.getElementById('calciumModalCanvasID') as HTMLCanvasElement;
    let calciumModalCtx = calciumCanvasModal.getContext('2d');
    let calciumChart = new Chart(calciumModalCtx, modalData);
    calciumChart.destroy();
    calciumChart = new Chart(calciumModalCtx, modalData);
    /*calcium canvas modal popup logic end*/
    /*carbonate canvas modal popup logic*/
    $('#carbonateModalCanvasID').remove(); // this is my <canvas> element
    $('#carbonateModalCanvas-container').append('<canvas id="carbonateModalCanvasID"><canvas>');
    let carbonateCanvasModal = document.getElementById('carbonateModalCanvasID') as HTMLCanvasElement;
    let carbonateModalCtx = carbonateCanvasModal.getContext('2d');
    let carbonateChart = new Chart(carbonateModalCtx, modalData);
    carbonateChart.destroy();
    carbonateChart = new Chart(carbonateModalCtx, modalData);
    /*carbonate canvas modal popup logic end*/
    /*hardness canvas modal popup logic*/
    $('#hardnessModalCanvasID').remove(); // this is my <canvas> element
    $('#hardnessModalCanvas-container').append('<canvas id="hardnessModalCanvasID"><canvas>');
    let hardnessCanvasModal = document.getElementById('hardnessModalCanvasID') as HTMLCanvasElement;
    let hardnessModalCtx = hardnessCanvasModal.getContext('2d');
    let hardnessChart = new Chart(hardnessModalCtx, modalData);
    hardnessChart.destroy();
    hardnessChart = new Chart(hardnessModalCtx, modalData);
    /*hardness canvas modal popup logic end*/
    /*magnesium canvas modal popup logic*/
    $('#magnesiumModalCanvasID').remove(); // this is my <canvas> element
    $('#magnesiumModalCanvas-container').append('<canvas id="magnesiumModalCanvasID"><canvas>');
    let magnesiumCanvasModal = document.getElementById('magnesiumModalCanvasID') as HTMLCanvasElement;
    let magnesiumModalCtx = magnesiumCanvasModal.getContext('2d');
    let magnesiumChart = new Chart(magnesiumModalCtx, modalData);
    magnesiumChart.destroy();
    magnesiumChart = new Chart(magnesiumModalCtx, modalData);
    /*magnesium canvas modal popup logic end*/
    /*nitrite canvas modal popup logic*/
    $('#nitriteModalCanvasID').remove(); // this is my <canvas> element
    $('#nitriteModalCanvas-container').append('<canvas id="nitriteModalCanvasID"><canvas>');
    let nitriteCanvasModal = document.getElementById('nitriteModalCanvasID') as HTMLCanvasElement;
    let nitriteModalCtx = nitriteCanvasModal.getContext('2d');
    let nitriteChart = new Chart(nitriteModalCtx, modalData);
    nitriteChart.destroy();
    nitriteChart = new Chart(nitriteModalCtx, modalData);
    /*nitrite canvas modal popup logic end*/
    /*tds canvas modal popup logic*/
    $('#tdsModalCanvasID').remove(); // this is my <canvas> element
    $('#tdsModalCanvas-container').append('<canvas id="tdsModalCanvasID"><canvas>');
    let tdsCanvasModal = document.getElementById('tdsModalCanvasID') as HTMLCanvasElement;
    let tdsModalCtx = tdsCanvasModal.getContext('2d');
    let tdsChart = new Chart(tdsModalCtx, modalData);
    tdsChart.destroy();
    tdsChart = new Chart(tdsModalCtx, modalData);
    /*tds canvas modal popup logic end*/
    /*tan canvas modal popup logic*/
    $('#tanModalCanvasID').remove(); // this is my <canvas> element
    $('#tanModalCanvas-container').append('<canvas id="tanModalCanvasID"><canvas>');
    let tanCanvasModal = document.getElementById('tanModalCanvasID') as HTMLCanvasElement;
    let tanModalCtx = tanCanvasModal.getContext('2d');
    let tanChart = new Chart(tanModalCtx, modalData);
    tanChart.destroy();
    tanChart = new Chart(tanModalCtx, modalData);
    /*tds canvas modal popup logic end*/
    /*turbidity canvas modal popup logic*/
    $('#turbidityModalCanvasID').remove(); // this is my <canvas> element
    $('#turbidityModalCanvas-container').append('<canvas id="turbidityModalCanvasID"><canvas>');
    let turbidityCanvasModal = document.getElementById('turbidityModalCanvasID') as HTMLCanvasElement;
    let turbidityModalCtx = turbidityCanvasModal.getContext('2d');
    let turbidityChart = new Chart(turbidityModalCtx, modalData);
    turbidityChart.destroy();
    turbidityChart = new Chart(turbidityModalCtx, modalData);
    /*turbidity canvas modal popup logic end*/
    /*orp canvas modal popup logic*/
    $('#orpModalCanvasID').remove(); // this is my <canvas> element
    $('#orpModalCanvas-container').append('<canvas id="orpModalCanvasID"><canvas>');
    let orpCanvasModal = document.getElementById('orpModalCanvasID') as HTMLCanvasElement;
    let orpModalCtx = orpCanvasModal.getContext('2d');
    let orpChart = new Chart(orpModalCtx, modalData);
    orpChart.destroy();
    orpChart = new Chart(orpModalCtx, modalData);
    /*orp canvas modal popup logic end*/
    /*bicarbonate canvas modal popup logic*/
    $('#bicarbonateModalCanvasID').remove(); // this is my <canvas> element
    $('#bicarbonateModalCanvas-container').append('<canvas id="bicarbonateModalCanvasID"><canvas>');
    let bicarbonateCanvasModal = document.getElementById('bicarbonateModalCanvasID') as HTMLCanvasElement;
    let bicarbonateModalCtx = bicarbonateCanvasModal.getContext('2d');
    let bicarbonateChart = new Chart(bicarbonateModalCtx, modalData);
    bicarbonateChart.destroy();
    bicarbonateChart = new Chart(bicarbonateModalCtx, modalData);
    /*bicarbonate canvas modal popup logic end*/

    /*abw canvas modal popup logic*/
    $('#abwModalCanvasID').remove(); // this is my <canvas> element
    $('#abwModalCanvas-container').append('<canvas id="abwModalCanvasID"><canvas>');
    let abwCanvasModal = document.getElementById('abwModalCanvasID') as HTMLCanvasElement;
    let abwModalCtx = abwCanvasModal.getContext('2d');
    let abwChart = new Chart(abwModalCtx, modalData);
    abwChart.destroy();
    abwChart = new Chart(abwModalCtx, modalData);
    /*abw canvas modal popup logic end*/
    /*adg canvas modal popup logic*/
    $('#adgModalCanvasID').remove(); // this is my <canvas> element
    $('#adgModalCanvas-container').append('<canvas id="adgModalCanvasID"><canvas>');
    let adgCanvasModal = document.getElementById('adgModalCanvasID') as HTMLCanvasElement;
    let adgModalCtx = adgCanvasModal.getContext('2d');
    let adgChart = new Chart(adgModalCtx, modalData);
    adgChart.destroy();
    adgChart = new Chart(adgModalCtx, modalData);
    /*adg canvas modal popup logic end*/
    /*sr canvas modal popup logic*/
    $('#srModalCanvasID').remove(); // this is my <canvas> element
    $('#srModalCanvas-container').append('<canvas id="srModalCanvasID"><canvas>');
    let srCanvasModal = document.getElementById('srModalCanvasID') as HTMLCanvasElement;
    let srModalCtx = srCanvasModal.getContext('2d');
    let srChart = new Chart(srModalCtx, modalData);
    srChart.destroy();
    srChart = new Chart(srModalCtx, modalData);
    /*sr canvas modal popup logic end*/
    /*tbm canvas modal popup logic*/
    $('#tbmModalCanvasID').remove(); // this is my <canvas> element
    $('#tbmModalCanvas-container').append('<canvas id="tbmModalCanvasID"><canvas>');
    let tbmCanvasModal = document.getElementById('tbmModalCanvasID') as HTMLCanvasElement;
    let tbmModalCtx = tbmCanvasModal.getContext('2d');
    let tbmChart = new Chart(tbmModalCtx, modalData);
    tbmChart.destroy();
    tbmChart = new Chart(tbmModalCtx, modalData);
    /*tbm canvas modal popup logic end*/
    /*dcf canvas modal popup logic*/
    $('#dcfModalCanvasID').remove(); // this is my <canvas> element
    $('#dcfModalCanvas-container').append('<canvas id="dcfModalCanvasID"><canvas>');
    let dcfCanvasModal = document.getElementById('dcfModalCanvasID') as HTMLCanvasElement;
    let dcfModalCtx = dcfCanvasModal.getContext('2d');
    let dcfChart = new Chart(dcfModalCtx, modalData);
    dcfChart.destroy();
    dcfChart = new Chart(dcfModalCtx, modalData);
    /*dcf canvas modal popup logic end*/
    /*fcr canvas modal popup logic*/
    $('#fcrModalCanvasID').remove(); // this is my <canvas> element
    $('#fcrModalCanvas-container').append('<canvas id="fcrModalCanvasID"><canvas>');
    let fcrCanvasModal = document.getElementById('fcrModalCanvasID') as HTMLCanvasElement;
    let fcrModalCtx = fcrCanvasModal.getContext('2d');
    let fcrChart = new Chart(fcrModalCtx, modalData);
    fcrChart.destroy();
    fcrChart = new Chart(fcrModalCtx, modalData);
    /*fcr canvas modal popup logic end*/
  }

  /**
   * @method bicarbonateChart
   * @description Renders the Bicarbonate line chart.
   * @param {any[]} bicarbonateDataSets - Datasets for bicarbonate.
   * @param {string[]} labels - X-axis labels.
   */
  public bicarbonateChart(bicarbonateDataSets: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], labels: any[]) {
    $('#bicarbonateCanvasID').remove(); // this is my <canvas> element
    $('#bicarbonateCanvas-container').append('<canvas id="bicarbonateCanvasID"><canvas>');
    let canvas = document.getElementById('bicarbonateCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.biCarbonateChartModalData = {
      type: 'line',
      data: {
        labels: labels,
        datasets: bicarbonateDataSets,
      },
      options: {
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'Bi-Carbonate',
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'BiCarbonate');
            }
          }
        }
      }
    };
    let chart = new Chart(ctx, this.biCarbonateChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.biCarbonateChartModalData);
  }

  /**
   * @method tempChart
   * @description Renders the Temperature line chart.
   * @param {any[]} temperatureDataSets - Datasets for temperature.
   * @param {string[]} label - X-axis labels.
   */
  public tempChart(temperatureDataSets: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], label: any[]) {
    $('#tempCanvasID').remove(); // this is my <canvas> element
    $('#tempCanvas-container').append('<canvas id="tempCanvasID"><canvas>');
    let canvas = document.getElementById('tempCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.tempChartModalData = {
      type: 'line',
      data: {
        labels: label,
        datasets: temperatureDataSets,
      },
      options: {
        legend: {display: false},
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'Temperature',
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'temperature');
            }
          }
        },
      }
    };
    let chart = new Chart(ctx, this.tempChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.tempChartModalData);
  }

  /**
   * @method salinityChart
   * @description Renders the Salinity line chart.
   * @param {any[]} salinityDataSets - Datasets for salinity.
   * @param {string[]} label - X-axis labels.
   */
  public salinityChart(salinityDataSets: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], label: any[]) {
    $('#salinityCanvasID').remove(); // this is my <canvas> element
    $('#salinityCanvas-container').append('<canvas id="salinityCanvasID"><canvas>');
    let canvas = document.getElementById('salinityCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.salinityChartModalData = {
      type: 'line',
      data: {
        labels: label,
        datasets: salinityDataSets,
      },
      options: {
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'Salinity',
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'salinity');
            }
          }
        }
      }
    };
    let chart = new Chart(ctx, this.salinityChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.salinityChartModalData);
  }

  /**
   * @method ammoniaChart
   * @description Renders the Ammonia line chart.
   * @param {any[]} ammoniaDataSets - Datasets for ammonia.
   * @param {string[]} label - X-axis labels.
   */
  public ammoniaChart(ammoniaDataSets: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], label: any[]) {
    $('#ammoniaCanvasID').remove(); // this is my <canvas> element
    $('#ammoniaCanvas-container').append('<canvas id="ammoniaCanvasID"><canvas>');
    let canvas = document.getElementById('ammoniaCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.ammoniaChartModalData = {
      type: 'line',
      data: {
        labels: label,
        datasets: ammoniaDataSets,
      },
      options: {
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'U Ammonia',
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'uam');
            }
          }
        }
      }
    };
    let chart = new Chart(ctx, this.ammoniaChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.ammoniaChartModalData);
  }

  /**
   * @method carbonateChart
   * @description Renders the Carbonate line chart.
   * @param {any[]} carbonateDataSets - Datasets for carbonate.
   * @param {string[]} label - X-axis labels.
   */
  public carbonateChart(carbonateDataSets: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], label: any[]) {
    $('#carbonateCanvasID').remove(); // this is my <canvas> element
    $('#carbonateCanvas-container').append('<canvas id="carbonateCanvasID"><canvas>');
    let canvas = document.getElementById('carbonateCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.carbonateChartModalData = {
      type: 'line',
      data: {
        labels: label,
        datasets: carbonateDataSets,
      },
      options: {
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'Carbonate',
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'Carbonate');
            }
          }
        }
      }
    };
    let chart = new Chart(ctx, this.carbonateChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.carbonateChartModalData);
  }

  /**
   * @method doChart
   * @description Renders the DO (Dissolved Oxygen) line chart.
   * @param {any[]} doDataSets - Datasets for DO.
   * @param {string[]} label - X-axis labels.
   */
  public doChart(doDataSets: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], label: any[]) {
    $('#doCanvasID').remove(); // this is my <canvas> element
    $('#doCanvas-container').append('<canvas id="doCanvasID"><canvas>');
    let canvas = document.getElementById('doCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.doChartModalData = {
      type: 'line',
      data: {
        labels: label,
        datasets: doDataSets,
      },
      options: {
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'Do2',
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'do2');
            }
          }
        }
      }
    };
    let chart = new Chart(ctx, this.doChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.doChartModalData);
  }

  /**
   * @method magnesiumChart
   * @description Renders the Magnesium line chart.
   * @param {any[]} magnesiumDataSets - Datasets for magnesium.
   * @param {string[]} label - X-axis labels.
   */
  public magnesiumChart(magnesiumDataSets: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], label: any[]) {
    $('#magnesiumCanvasID').remove(); // this is my <canvas> element
    $('#magnesiumCanvas-container').append('<canvas id="magnesiumCanvasID"><canvas>');
    let canvas = document.getElementById('magnesiumCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.magnesiumChartModalData = {
      type: 'line',
      data: {
        labels: label,
        datasets: magnesiumDataSets,
      },
      options: {
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'Magnesium',
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'Magnesium');
            }
          }
        }
      }
    };
    let chart = new Chart(ctx, this.magnesiumChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.magnesiumChartModalData);
  }

  /**
   * @method turbidityChart
   * @description Renders the Turbidity line chart.
   * @param {any[]} turbidityDataSets - Datasets for turbidity.
   * @param {string[]} labels - X-axis labels.
   */
  public turbidityChart(turbidityDataSets: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], labels: any[]) {
    $('#turbidityCanvasID').remove(); // this is my <canvas> element
    $('#turbidityCanvas-container').append('<canvas id="turbidityCanvasID"><canvas>');
    let canvas = document.getElementById('turbidityCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.turbidityChartModalData = {
      type: 'line',
      data: {
        labels: labels,
        datasets: turbidityDataSets,
      },
      options: {
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'Turbidity',
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'turbidity');
            }
          }
        }
      }
    };
    let chart = new Chart(ctx, this.turbidityChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.turbidityChartModalData);
  }

  /**
   * @method tanChart
   * @description Renders the TAN (Total Ammonia Nitrogen) line chart.
   * @param {any[]} tanDataSets - Datasets for TAN.
   * @param {string[]} labels - X-axis labels.
   */
  public tanChart(tanDataSets: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], labels: any[]) {
    $('#tanCanvasID').remove(); // this is my <canvas> element
    $('#tanCanvas-container').append('<canvas id="tanCanvasID"><canvas>');
    let canvas = document.getElementById('tanCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.tanChartModalData = {
      type: 'line',
      data: {
        labels: labels,
        datasets: tanDataSets,
      },
      options: {
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'TAN',
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'tan');
            }
          }
        }
      }
    };
    let chart = new Chart(ctx, this.tanChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.tanChartModalData);
  }

  /**
   * @method tdsChart
   * @description Renders the TDS (Total Dissolved Solids) line chart.
   * @param {any[]} tdsDataSets - Datasets for TDS.
   * @param {string[]} labels - X-axis labels.
   */
  public tdsChart(tdsDataSets: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], labels: any[]) {
    $('#tdsCanvasID').remove(); // this is my <canvas> element
    $('#tdsCanvas-container').append('<canvas id="tdsCanvasID"><canvas>');
    let canvas = document.getElementById('tdsCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.tdsChartModalData = {
      type: 'line',
      data: {
        labels: labels,
        datasets: tdsDataSets,
      },
      options: {
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'TDS',
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'tds');
            }
          }
        }
      }
    };
    let chart = new Chart(ctx, this.tdsChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.tdsChartModalData);
  }

  /**
   * @method alkalinityChart
   * @description Renders the Alkalinity line chart.
   * @param {any[]} alkalinityDataSets - Datasets for alkalinity.
   * @param {string[]} label - X-axis labels.
   */
  public alkalinityChart(alkalinityDataSets: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], label: any[]) {
    $('#alkalinityCanvasID').remove(); // this is my <canvas> element
    $('#alkalinityCanvas-container').append('<canvas id="alkalinityCanvasID"><canvas>');
    let canvas = document.getElementById('alkalinityCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.alkalinityChartModalData = {
      type: 'line',
      data: {
        labels: label,
        datasets: alkalinityDataSets,
      },
      options: {
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'Alkalinity',
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'alkalinity');
            }
          }
        }
      }
    };
    let chart = new Chart(ctx, this.alkalinityChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.alkalinityChartModalData);
  }

  /**
   * @method bioflocChart
   * @description Renders the Bio-Floc line chart.
   * @param {any[]} bioflockDataSets - Datasets for bio-floc.
   * @param {string[]} label - X-axis labels.
   */
  public bioflocChart(bioflockDataSets: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], label: any[]) {
    $('#bioflocCanvasID').remove(); // this is my <canvas> element
    $('#bioflocCanvas-container').append('<canvas id="bioflocCanvasID"><canvas>');
    let canvas = document.getElementById('bioflocCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.bioflocChartModalData = {
      type: 'line',
      data: {
        labels: label,
        datasets: bioflockDataSets,
      },
      options: {
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'Bio-Floc',
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'Bio_Floc');
            }
          }
        }
      }
    };
    let chart = new Chart(ctx, this.bioflocChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.bioflocChartModalData);
  }

  /**
   * @method calciumChart
   * @description Renders the Calcium line chart.
   * @param {any[]} calciumDataSets - Datasets for calcium.
   * @param {string[]} label - X-axis labels.
   */
  public calciumChart(calciumDataSets: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], label: any[]) {
    $('#calciumCanvasID').remove(); // this is my <canvas> element
    $('#calciumCanvas-container').append('<canvas id="calciumCanvasID"><canvas>');
    let canvas = document.getElementById('calciumCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.calciumChartModalData = {
      type: 'line',
      data: {
        labels: label,
        datasets: calciumDataSets,
      },
      options: {
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'Calcium',
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'Calcium');
            }
          }
        }
      }
    };
    let chart = new Chart(ctx, this.calciumChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.calciumChartModalData);
  }

  /**
   * @method hardnessChart
   * @description Renders the Hardness line chart.
   * @param {any[]} hardnessDataSets - Datasets for hardness.
   * @param {string[]} label - X-axis labels.
   */
  public hardnessChart(hardnessDataSets: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], label: any[]) {
    $('#hardnessCanvasID').remove(); // this is my <canvas> element
    $('#hardnessCanvas-container').append('<canvas id="hardnessCanvasID"><canvas>');
    let canvas = document.getElementById('hardnessCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.hardnessChartModalData = {
      type: 'line',
      data: {
        labels: label,
        datasets: hardnessDataSets,
      },
      options: {
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'Hardness',
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'Hardness');
            }
          }
        }
      }
    };
    let chart = new Chart(ctx, this.hardnessChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.hardnessChartModalData);
  }

  /**
   * @method nitriteChart
   * @description Renders the Nitrite line chart.
   * @param {any[]} nitriteDataSets - Datasets for nitrite.
   * @param {string[]} labels - X-axis labels.
   */
  public nitriteChart(nitriteDataSets: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], labels: any[]) {
    $('#nitriteCanvasID').remove(); // this is my <canvas> element
    $('#nitriteCanvas-container').append('<canvas id="nitriteCanvasID"><canvas>');
    let canvas = document.getElementById('nitriteCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.nitriteChartModalData = {
      type: 'line',
      data: {
        labels: labels,
        datasets: nitriteDataSets,
      },
      options: {
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'Nitrite',
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'nitrite');
            }
          }
        }
      }
    };
    let chart = new Chart(ctx, this.nitriteChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.nitriteChartModalData);
  }

  /**
   * @method orpChart
   * @description Renders the ORP (Oxidation-Reduction Potential) line chart.
   * @param {any[]} orpDataSets - Datasets for ORP.
   * @param {string[]} labels - X-axis labels.
   */
  public orpChart(orpDataSets: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], labels: any[]) {
    $('#orpCanvasID').remove(); // this is my <canvas> element
    $('#orpCanvas-container').append('<canvas id="orpCanvasID"><canvas>');
    let canvas = document.getElementById('orpCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.orpChartModalData = {
      type: 'line',
      data: {
        labels: labels,
        datasets: orpDataSets,
      },
      options: {
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'ORP',
              fontColor: this.getRandomColor()
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'orp');
            }
          }
        }
      }
    };
    let chart = new Chart(ctx, this.orpChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.orpChartModalData);
  }

  /**
   * @method abwChart
   * @description Renders the ABW (Average Body Weight) line chart.
   * @param {any[]} abwDataSet - Datasets for ABW.
   * @param {string[]} label - X-axis labels.
   */
  public abwChart(abwDataSet: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], label: any[]) {
    $('#abwCanvasID').remove(); // this is my <canvas> element
    $('#abwCanvas-container').append('<canvas id="abwCanvasID"><canvas>');
    let canvas = document.getElementById('abwCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.abwChartModalData = {
      type: 'line',
      data: {
        labels: label, // 2021-09-02
        datasets: abwDataSet, // [9,8, -1]
      },
      options: {
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'ABW',
              fontColor: this.getRandomColor()
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'abw');
            }
          }
        }
      }
    };
    let chart = new Chart(ctx, this.abwChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.abwChartModalData);
  }

  /*adg*/
  /**
   * @method adgChart
   * @description Renders the ADG (Average Daily Growth) line chart.
   * @param {any[]} adgDataSet - Datasets for ADG.
   * @param {string[]} label - X-axis labels.
   */
  public adgChart(adgDataSet: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], label: any[]) {
    $('#adgCanvasID').remove(); // this is my <canvas> element
    $('#adgCanvas-container').append('<canvas id="adgCanvasID"><canvas>');
    let canvas = document.getElementById('adgCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.adgChartModalData = {
      type: 'line',
      data: {
        labels: label, // 2021-09-02
        datasets: adgDataSet, // [9,8, -1]
      },
      options: {
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'ADG',
              fontColor: this.getRandomColor()
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'adg');
            }
          }
        }
      }
    };
    let chart = new Chart(ctx, this.adgChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.adgChartModalData);
  }

  /*fcr*/
  /**
   * @method fcrChart
   * @description Renders the FCR (Feed Conversion Ratio) line chart.
   * @param {any[]} fcrDataSet - Datasets for FCR.
   * @param {string[]} label - X-axis labels.
   */
  public fcrChart(fcrDataSet: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], label: any[]) {
    $('#fcrCanvasID').remove(); // this is my <canvas> element
    $('#fcrCanvas-container').append('<canvas id="fcrCanvasID"><canvas>');
    let canvas = document.getElementById('fcrCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.fcrChartModalData = {
      type: 'line',
      data: {
        labels: label, // 2021-09-02
        datasets: fcrDataSet, // [9,8, -1]
      },
      options: {
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'FCR',
              fontColor: this.getRandomColor()
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'FCR');
            }
          }
        }
      }
    };
    let chart = new Chart(ctx, this.fcrChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.fcrChartModalData);
  }

  /*total biomass*/
  /**
   * @method tbmChart
   * @description Renders the Total Biomass line chart.
   * @param {any[]} tbmDataSet - Datasets for Total Biomass.
   * @param {string[]} label - X-axis labels.
   */
  public tbmChart(tbmDataSet: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], label: any[]) {
    $('#tbmCanvasID').remove(); // this is my <canvas> element
    $('#tbmCanvas-container').append('<canvas id="tbmCanvasID"><canvas>');
    let canvas = document.getElementById('tbmCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.tbmChartModalData = {
      type: 'line',
      data: {
        labels: label, // 2021-09-02
        datasets: tbmDataSet, // [9,8, -1]
      },
      options: {
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'Total Biomass',
              fontColor: this.getRandomColor()
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'total_biomass');
            }
          }
        }
      }
    };
    let chart = new Chart(ctx, this.tbmChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.tbmChartModalData);
  }

  /*sr*/
  /**
   * @method srChart
   * @description Renders the Survival Rate line chart.
   * @param {any[]} srDataSet - Datasets for Survival Rate.
   * @param {string[]} label - X-axis labels.
   */
  public srChart(srDataSet: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], label: any[]) {
    $('#srCanvasID').remove(); // this is my <canvas> element
    $('#srCanvas-container').append('<canvas id="srCanvasID"><canvas>');
    let canvas = document.getElementById('srCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.srChartModalData = {
      type: 'line',
      data: {
        labels: label, // 2021-09-02
        datasets: srDataSet, // [9,8, -1]
      },
      options: {
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'Survival Rate',
              fontColor: this.getRandomColor()
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'survival_rate');
            }
          }
        }
      }
    };
    let chart = new Chart(ctx, this.srChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.srChartModalData);
  }

  /*day cumulative feed*/
  /**
   * @method dcfChart
   * @description Renders the Cumulative Feed line chart.
   * @param {any[]} dcfDataSet - Datasets for Cumulative Feed.
   * @param {string[]} label - X-axis labels.
   */
  public dcfChart(dcfDataSet: (string | any[][] | {
      data: any[][]; label: any;
      // label: pondName,
      tankId: any; borderColor: string; fill: boolean; spanGaps: boolean;
    })[], label: any[]) {
    $('#dcfCanvasID').remove(); // this is my <canvas> element
    $('#dcfCanvas-container').append('<canvas id="dcfCanvasID"><canvas>');
    let canvas = document.getElementById('dcfCanvasID') as HTMLCanvasElement;
    let ctx = canvas.getContext('2d');
    this.dcfChartModalData = {
      type: 'line',
      data: {
        labels: label, // 2021-09-02
        datasets: dcfDataSet, // [9,8, -1]
      },
      options: {
        title: {
          display: true,
        },
        scales: {
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'Cumulative Feed',
              fontColor: this.getRandomColor()
            }
          }],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click'],
        tooltips: {
          callbacks: {
            title: function(): any {
              return null;
            },
            label: (tooltipItem: any, data: any) => {
              let tank = data.datasets[tooltipItem.datasetIndex].tankId;
              let date = tooltipItem.xLabel;
              return this.getTooltipData(date, tank, 'cumulative_feed');
            }
          }
        }
      }
    };
    let chart = new Chart(ctx, this.dcfChartModalData);
    chart.destroy();
    chart = new Chart(ctx, this.dcfChartModalData);
  }

  /*Random color generator*/
  /**
   * @method getRandomColor
   * @description Generates a random hexadecimal color code.
   * @returns {string} Random hex color code.
   */
  public getRandomColor() {
    let letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  /**
   * @method getTooltipData
   * @description Generates tooltip content for charts based on date, tank, and type.
   * @param {string} date - The date of the data point.
   * @param {string} tank - The tank ID.
   * @param {string} type - The parameter type.
   * @returns {string} Tooltip string.
   */
  public getTooltipData(date: any, tank: any, type: string) {
    if (this.tabIndex == 0) {
      let str = '';
      let res = _.filter(this.metrics, {pond_id: tank, date: date});
      if (res && res.length > 0) {
        let temprery = [];
        for (let u = 0; u < res.length; u++) {
          let eachRes = res[u];
          let typeValue = (eachRes as any)[type];
          temprery.push(typeValue);
          /*for (let v = 0; v < eachRes.Readings.length; v++) {
            let each = eachRes.Readings[v];
            let typeValue = each[type];
            temprery.push(typeValue);
          }
          ;*/
          /*let typeValue = eachRes[type];
          temprery.push(typeValue);*/
        }
        if (temprery && temprery.length > 0) {
          str = temprery.join(',');
        }
      }
      return str;
    } else if (this.tabIndex == 1) {
      let growthstr = '';
      let growthres = _.filter(this.chartGlobalData, {pond_id: tank, date: date});
      if (growthres && growthres.length > 0) {
        let growthtemprery = [];
        for (let growth = 0; growth < growthres.length; growth++) {
          let growtheachRes = growthres[growth];
          let typeValue = growtheachRes[type];
          growthtemprery.push(typeValue);
        }
        if (growthtemprery && growthtemprery.length > 0) {
          growthstr = growthtemprery.join(',');
        }
      }
      return growthstr;
    }
  }

  /**
   * @method compareChart
   * @description Renders the comparison chart with two datasets on dual Y-axes.
   * @param {any[]} dataset1 - First dataset (Water Parameter).
   * @param {any[]} dataset2 - Second dataset (Growth Parameter).
   * @param {string[]} label - X-axis labels.
   */
  public compareChart(dataset1: any, dataset2: any, label: any[]) {
    const ctx = this.compareCanvas.nativeElement.getContext('2d');
    let gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, '#0f4573');
    gradient.addColorStop(0.5, '#51b7c5');
    let waterParam = _.startCase(this.readingParam);
    let growthParam = _.startCase(this.growthParam);
    this.barChart = new Chart(this.compareCanvas.nativeElement, {
      type: 'line',
      data: {
        labels: label,
        datasets: [{
          data: dataset1,
          fill: false,
          spanGaps: true,
          label: waterParam,
          yAxisID: 'y',
          // backgroundColor: this.getRandomColor(),
          borderColor: '#b80d2f',
          borderCapStyle: 'butt',
          borderDash: [],
          borderDashOffset: 0.0,
          borderJoinStyle: 'miter',
          pointBorderColor: '#b80d2f',
          pointBackgroundColor: '#fff',
          pointBorderWidth: 5,
          pointHoverRadius: 5,
          pointHoverBackgroundColor: '#b80d2f',
          pointHoverBorderColor: '#b80d2f',
          pointHoverBorderWidth: 2,
          pointRadius: 1,
          pointHitRadius: 10,
        }, {
          data: dataset2,
          fill: false,
          spanGaps: true,
          // backgroundColor: this.getRandomColor(),
          label: growthParam,
          yAxisID: 'y1',
          borderColor: '#0f4573',
          borderCapStyle: 'butt',
          borderDash: [],
          borderDashOffset: 0.0,
          borderJoinStyle: 'miter',
          pointBorderColor: 'rgba(75,192,192,1)',
          pointBackgroundColor: '#fff',
          pointBorderWidth: 5,
          pointHoverRadius: 5,
          pointHoverBackgroundColor: 'rgba(75,192,192,1)',
          pointHoverBorderColor: 'rgba(220,220,220,1)',
          pointHoverBorderWidth: 2,
          pointRadius: 1,
          pointHitRadius: 10,
        }]
      },
      options: {
        scales: {
          yAxes: [
            {
              id: 'y',
              type: 'linear',
              position: 'left',
              scaleLabel: {
                display: true,
                labelString: waterParam
              }
            }, {
              id: 'y1',
              type: 'linear',
              position: 'right',
              scaleLabel: {
                display: true,
                labelString: growthParam
              }
            }
          ],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click']
      }
    });
  }

  /*compare tabindex == 2*/
  /**
   * @method vsParamChange
   * @description Handles parameter selection changes for comparison in the Compare tab.
   * @param {string} param - The parameter name.
   * @param {string} ngmodalval - The ngModel property name for the checkbox.
   */
  public vsParamChange(param: any, ngmodalval: string | number) {
    if (!this.temp) {
      this.temp = [];
    }
    if (this.temp && this.temp.length == 2 && (this as any)[ngmodalval]) {
      this.toastrService.error('Allow only two params', '', {
        timeOut: 2000
      });
      (this as any)[ngmodalval] = false;
      this.unchecktype(param);
    } else {
      // if (param =='pH' && event.target.value =='on') {
      if ((this as any)[ngmodalval]) {
        this.temp.push(param);
      } else {
        _.remove(this.temp, function(n) {
          return n == param;
        });
      }
      if (this.temp.length > 0) {
        if (this.temp.length > 1) {
          this.readingParam = this.temp[0];
          this.growthParam = this.temp[1];
        }
        if (this.temp.length == 1) {
          this.readingParam = this.temp[0];
          this.growthParam = '';
        }
        this.prepareChart();
      } else {
        this.readingParam = '';
        this.growthParam = '';
      }
    }
  }

  /**
   * @method unchecktype
   * @description Unchecks a specific parameter checkbox based on the parameter name.
   * @param {string} param - The parameter name.
   */
  public unchecktype(param: string) {
    if (param == 'pH') {
      this['phreading'] = false;
    } else if (param == 'do2') {
      this['doreading'] = false;
    } else if (param == 'salinity') {
      this['salinityreading'] = false;
    } else if (param == 'temperature') {
      this['tempreading'] = false;
    } else if (param == 'tan') {
      this['tanreading'] = false;
    } else if (param == 'uammonia') {
      this['uammoniareading'] = false;
    } else if (param == 'Nitrite') {
      this['nitritereading'] = false;
    } else if (param == 'Alkalinity') {
      this['alkalinityreading'] = false;
    } else if (param == 'Bio_Floc') {
      this['bioflocreading'] = false;
    } else if (param == 'abw') {
      this['abwGrowth'] = false;
    } else if (param == 'adg') {
      this['adgGrowth'] = false;
    } else if (param == 'survival_rate') {
      this['srGrowth'] = false;
    } else if (param == 'total_biomass') {
      this['tbmGrowth'] = false;
    } else if (param == 'FCR') {
      this['fcrGrowth'] = false;
    } else if (param == 'cumulative_feed') {
      this['dcfGrowth'] = false;
    }
  }

  /*click on add to compare button*/
  /**
   * @method compareButton
   * @description Adds a parameter to the comparison list when clicked.
   * @param {string} param - The parameter name.
   */
  public compareButton(param: string) {
    this.viewclicked = true;
    if (!this.compareTemp) {
      this.compareTemp = []; // ['ph']
    }
    if (this.compareTemp && this.compareTemp.length == 2) {
      this.toastrService.error('allow only two parameters', '', {
        timeOut: 2000
      });
      this.unchecktype(param);
      return;
    } else if (this.compareTemp) {
      if (this.compareTemp.length != 0) {
        for (let i = 0; i < this.compareTemp.length; i++) {
          if (param == this.compareTemp[i]) {
            return;
          } else {
            this.compareTemp.push(param);
          }
        }
      } else {
        this.compareTemp.push(param);
      }
    }
  }

  /*click on view button display graph*/
  /**
   * @method viewGraph
   * @description Opens the comparison graph modal for selected parameters.
   * @param {string[]} selectedParam - Array of selected parameters.
   */
  public viewGraph(selectedParam: any) {
    this.viewclicked = true;
    this.preparePopUpModal();
    if (this.checkedIDs.length > 1) {
      this.toastrService.error('Compare only one tank for two parameters', '', {
        timeOut: 4000
      });
      return;
    }
    this.metric.comparepondId = this.checkedIDs[0];
    this.readingParam = selectedParam[0];
    this.growthParam = selectedParam[1];
    $('#myModal').modal('show');
    this.comparePrepareChart();
  }

  /**
   * @method preparePopUpModal
   * @description Sets boolean flags for popup modal checkboxes based on `compareTemp` array.
   */
  public preparePopUpModal() {
    if (this.compareTemp && this.compareTemp.indexOf('pH') != -1) {
      this.pHPopUpreading = true;
    } else {
      this.pHPopUpreading = false;
    }
    if (this.compareTemp && this.compareTemp.indexOf('temperature') != -1) {
      this.tempPopUpreading = true;
    } else {
      this.tempPopUpreading = false;
    }
    if (this.compareTemp && this.compareTemp.indexOf('do2') != -1) {
      this.doPopUpreading = true;
    } else {
      this.doPopUpreading = false;
    }
    if (this.compareTemp && this.compareTemp.indexOf('salinity') != -1) {
      this.salinityPopUpreading = true;
    } else {
      this.salinityPopUpreading = false;
    }
    if (this.compareTemp && this.compareTemp.indexOf('tan') != -1) {
      this.tanPopUpreading = true;
    } else {
      this.tanPopUpreading = false;
    }
    if (this.compareTemp && this.compareTemp.indexOf('uam') != -1) {
      this.uammoniaPopUpreading = true;
    } else {
      this.uammoniaPopUpreading = false;
    }
    if (this.compareTemp && this.compareTemp.indexOf('Nitrite') != -1) {
      this.nitritePopUpreading = true;
    } else {
      this.nitritePopUpreading = false;
    }
    if (this.compareTemp && this.compareTemp.indexOf('Alkalinity') != -1) {
      this.alkalinityPopUpreading = true;
    } else {
      this.alkalinityPopUpreading = false;
    }
    if (this.compareTemp && this.compareTemp.indexOf('Bio_Floc') != -1) {
      this.bioflocPopUpreading = true;
    } else {
      this.bioflocPopUpreading = false;
    }
    if (this.compareTemp && this.compareTemp.indexOf('abw') != -1) {
      this.abwPopUpGrowth = true;
    } else {
      this.abwPopUpGrowth = false;
    }
    if (this.compareTemp && this.compareTemp.indexOf('adg') != -1) {
      this.adgPopUpGrowth = true;
    } else {
      this.adgPopUpGrowth = false;
    }
    if (this.compareTemp && this.compareTemp.indexOf('survival_rate') != -1) {
      this.srPopUpGrowth = true;
    } else {
      this.srPopUpGrowth = false;
    }
    if (this.compareTemp && this.compareTemp.indexOf('total_biomass') != -1) {
      this.tbmPopUpGrowth = true;
    } else {
      this.tbmPopUpGrowth = false;
    }
    if (this.compareTemp && this.compareTemp.indexOf('FCR') != -1) {
      this.fcrPopUpGrowth = true;
    } else {
      this.fcrPopUpGrowth = false;
    }
    if (this.compareTemp && this.compareTemp.indexOf('cumulative_feed') != -1) {
      this.dcfPopUpGrowth = true;
    } else {
      this.dcfPopUpGrowth = false;
    }
  }

  /**
   * @method vsPopUpParamChange
   * @description Handles parameter selection changes within the popup modal.
   * @param {string} param - The parameter name.
   * @param {string} ngmodalval - The ngModel property name.
   */
  public vsPopUpParamChange(param: any, ngmodalval: string | number) {
    if (!this.compareTemp) {
      this.compareTemp = [];
    }
    if (this.compareTemp && this.compareTemp.length == 2 && (this as any)[ngmodalval]) {
      this.toastrService.error('Allow only two params', '', {
        timeOut: 2000
      });
      (this as any)[ngmodalval] = false;
      this.unCheckPopUpType(param);
    } else {
      // if (param =='pH' && event.target.value =='on') {
      if ((this as any)[ngmodalval]) {
        this.compareTemp.push(param);
      } else {
        _.remove(this.compareTemp, function(n) {
          return n == param;
        });
      }
      if (this.compareTemp.length > 0) {
        if (this.compareTemp.length > 1) {
          this.readingParam = this.compareTemp[0];
          this.growthParam = this.compareTemp[1];
        }
        if (this.compareTemp.length == 1) {
          this.readingParam = this.compareTemp[0];
          this.growthParam = '';
        }
        this.comparePrepareChart();
      } else {
        this.readingParam = '';
        this.growthParam = '';
      }
    }
  }

  /**
   * @method unCheckPopUpType
   * @description Unchecks a specific parameter checkbox in the popup modal.
   * @param {string} param - The parameter name.
   */
  public unCheckPopUpType(param: string) {
    if (param == 'pH') {
      this['pHPopUpreading'] = false;
    } else if (param == 'do2') {
      this['doPopUpreading'] = false;
    } else if (param == 'salinity') {
      this['salinityPopUpreading'] = false;
    } else if (param == 'temperature') {
      this['tempPopUpreading'] = false;
    } else if (param == 'tan') {
      this['tanPopUpreading'] = false;
    } else if (param == 'uammonia') {
      this['uammoniaPopUpreading'] = false;
    } else if (param == 'Nitrite') {
      this['nitritePopUpreading'] = false;
    } else if (param == 'Alkalinity') {
      this['alkalinityPopUpreading'] = false;
    } else if (param == 'Bio_Floc') {
      this['bioflocPopUpreading'] = false;
    } else if (param == 'abw') {
      this['abwPopUpGrowth'] = false;
    } else if (param == 'adg') {
      this['adgPopUpGrowth'] = false;
    } else if (param == 'survival_rate') {
      this['srPopUpGrowth'] = false;
    } else if (param == 'total_biomass') {
      this['tbmPopUpGrowth'] = false;
    } else if (param == 'FCR') {
      this['fcrPopUpGrowth'] = false;
    } else if (param == 'cumulative_feed') {
      this['dcfPopUpGrowth'] = false;
    }
  }

  /**
   * @method closepopup
   * @description Closes the popup modal and resets comparison state.
   */
  public closepopup() {
    this.viewclicked = false;
    this.compareTemp = [];
    this.readingParam = '';
    this.growthParam = '';
    this.metric.comparepondId = undefined;

  }

  /**
   * @method removeParam
   * @description Removes a parameter from the comparison list.
   * @param {number} i - Index of the parameter to remove.
   * @returns {boolean} True.
   */
  public removeParam(i: number) {
    this.compareTemp.splice(i, 1);
    this.toastrService.success('Param deleted successfully', '', {
      timeOut: 2000
    });
    return true;
  }

  /**
   * @method comparePrepareChart
   * @description Prepares data for the comparison chart if a pond is selected.
   */
  public comparePrepareChart() {
    if (this.metric.comparepondId) {
      this.popupNewMethod();
    } else {
      this.comparePopUpChartDisplay = false;
    }
  }

  /**
   * @method popupNewMethod
   * @description Processes data for the popup comparison chart.
   */
  public popupNewMethod() {
    let readings = this.popupchartGlobalData; // globaldata
    let labels = _.map(readings, 'date');
    labels = _.uniq(labels); // [2021-09-02, 2021-08-02]
    labels = _.sortBy(labels);
    let one: any;
    let two: any;
    let tanksData = _.groupBy(this.popupchartGlobalData, 'pond_id');
    let tankId = this.metric.comparepondId;
    if (tanksData) {
      let eachTankData = tanksData[tankId];
      /*campare graph*/
      if (eachTankData) {
        one = this.getDatasetNew(eachTankData, this.readingParam, labels);
        two = this.getDatasetNew(eachTankData, this.growthParam, labels);
      }
    }
    if (one || two) {
      this.comparePopUpChartDisplay = true;
      this.compareModalChart(one, two, labels);
    } else {
      this.comparePopUpChartDisplay = false;
    }
  }

  /**
   * @method compareModalChart
   * @description Renders the comparison chart inside the modal popup.
   * @param {any[]} dataset1 - First dataset.
   * @param {any[]} dataset2 - Second dataset.
   * @param {string[]} label - X-axis labels.
   */
  public compareModalChart(dataset1: any, dataset2: any, label: any[]) {
    const ctx = this.comparepopUpCanvas.nativeElement.getContext('2d');
    let gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, '#0f4573');
    gradient.addColorStop(0.5, '#51b7c5');
    let waterParam = _.startCase(this.readingParam);
    let growthParam = _.startCase(this.growthParam);
    this.barChart = new Chart(this.comparepopUpCanvas.nativeElement, {
      type: 'line',
      data: {
        labels: label,
        datasets: [{
          data: dataset1,
          fill: false,
          spanGaps: true,
          label: waterParam,
          yAxisID: 'y',
          // backgroundColor: this.getRandomColor(),
          borderColor: '#b80d2f',
          borderCapStyle: 'butt',
          borderDash: [],
          borderDashOffset: 0.0,
          borderJoinStyle: 'miter',
          pointBorderColor: '#b80d2f',
          pointBackgroundColor: '#fff',
          pointBorderWidth: 5,
          pointHoverRadius: 5,
          pointHoverBackgroundColor: '#b80d2f',
          pointHoverBorderColor: '#b80d2f',
          pointHoverBorderWidth: 2,
          pointRadius: 1,
          pointHitRadius: 10,
        }, {
          data: dataset2,
          fill: false,
          spanGaps: true,
          // backgroundColor: this.getRandomColor(),
          label: growthParam,
          yAxisID: 'y1',
          borderColor: '#0f4573',
          borderCapStyle: 'butt',
          borderDash: [],
          borderDashOffset: 0.0,
          borderJoinStyle: 'miter',
          pointBorderColor: 'rgba(75,192,192,1)',
          pointBackgroundColor: '#fff',
          pointBorderWidth: 5,
          pointHoverRadius: 5,
          pointHoverBackgroundColor: 'rgba(75,192,192,1)',
          pointHoverBorderColor: 'rgba(220,220,220,1)',
          pointHoverBorderWidth: 2,
          pointRadius: 1,
          pointHitRadius: 10,
        }]
      },
      options: {
        scales: {
          yAxes: [
            {
              id: 'y',
              type: 'linear',
              position: 'left',
              scaleLabel: {
                display: true,
                labelString: waterParam
              }
            }, {
              id: 'y1',
              type: 'linear',
              position: 'right',
              scaleLabel: {
                display: true,
                labelString: growthParam
              }
            }
          ],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'DOC'
            }
          }]
        },
        events: ['click']
      }
    });
  }
}
